import moment from 'moment'
import lodash from 'lodash'
import { krdetailConstants } from '../constants/krdetails.constants'
import { isWeeklyInterval } from './metric'
import i18next from "i18next"
import { formatNumber } from './numberFormat'

/*
    developer notes:
    we have 4 chart interval each stands for
    (interval): 1 daily, 2 weekly, 3 monthly, 4 quarterly
    Label format:
    daily, weekly: we show date. Ex. Jan 1.
    monthly: we show month only. Ex. Jan
    quarterly: we show the last month of the quarter. Q1 Ex Mar.
    Column width:
    we can have data group up to 7, so when data group > 3,
    we make the bar width smaller(pointWidth) for better UI
    (https://api.highcharts.com/highcharts/series.bar.pointWidth)
    currently we set the threshold 30 on data points, if the data points is > 30,
    to prevent data get squished together,
    we show the latest 30 data points on the chart on first load.
    (null value with timestamp count as a valid data point)
    don't use tickinterval, it will cause the x-axis label mis-alignment
    we set x-axis label by data points, use tickPositioner instead
    (https://api.highcharts.com/highcharts/xAxis.tickPositioner)
    above notes apply to both line chart and bar(column) chart
*/
// only used to help with tooltip alignment issues.  Should be TRUE in production
const SHOW_Y_AXIS = true;

const WOBO_NUMERIC_SYMBOL_DEFAULTS = ['k', 'M', 'B', 'T']

const getIntervalUnits = (interval) => {
  let units = [['day', [1]]]
  if (interval == krdetailConstants.INTERVAL_DAY) {
    // day
    units = [['day', [1]]]
  } else if (interval == krdetailConstants.INTERVAL_WEEK) {
    // week
    units = [['week', [1]]]
  } else if (interval == krdetailConstants.INTERVAL_MONTH) {
    // month
    units = [['month', [1]]]
  } else if (interval == krdetailConstants.INTERVAL_QUARTER) {
    // quarterly
    units = [['month', [3]]]
  } else if (interval == krdetailConstants.INTERVAL_BIWEEKLY) {
    // bi week
    units = [['week', [2]]]
  }
  return units
}

const getLastOfInterval = (interval) => {
  if (
    interval == krdetailConstants.INTERVAL_DAY ||
    interval == krdetailConstants.INTERVAL_WEEK ||
    interval == krdetailConstants.INTERVAL_BIWEEKLY
  ) {
    // day, week, bi week
    return 'day'
  } else if (interval == krdetailConstants.INTERVAL_MONTH) {
    // month
    return 'month'
  } else if (interval == krdetailConstants.INTERVAL_QUARTER) {
    // quarterly
    return 'quarter'
  }
}

const getTickInterval = (interval) => {
  let tickInterval = 7 * 24 * 36e5
  if (interval == krdetailConstants.INTERVAL_MONTH) {
    tickInterval = 30 * 24 * 36e5
  } else if (interval == krdetailConstants.INTERVAL_QUARTER) {
    tickInterval = 3 * 30 * 24 * 36e5
  } else if (interval == krdetailConstants.INTERVAL_DAY) {
    tickInterval = 24 * 36e5
  } else if (interval == krdetailConstants.INTERVAL_BIWEEKLY) {
    tickInterval = 14 * 24 * 36e5
  }
  return tickInterval
}

const findStandardTimeQuarterLabel = (time) => {
  const m = moment(time).format('M')

  if (m <= 3) {
    return 'Q1'
  } else if (m > 3 && m <= 6) {
    return 'Q2'
  } else if (m > 6 && m <= 9) {
    return 'Q3'
  } else if (m > 9 && m <= 12) {
    return 'Q4'
  }
  return null
}

const findQuarterLabel = (op = {}, time = null) => {
  const { childMitData, orgPeriods } = op
  const timestamp1 = time / 1000
  if (Array.isArray(orgPeriods) && orgPeriods.length) {
    const periods = JSON.parse(JSON.stringify(orgPeriods))
    const orgPeriodsArray = createOrgPeriodsObject(periods)
    const findQ = orgPeriodsArray.find(
      (q) => q.start <= parseInt(timestamp1) && parseInt(timestamp1) <= q.end
    )
    if (findQ) {
      return findQ._name
    }
  } else {
    if (childMitData) {
      const timestamp1 = time / 1000
      //console.log('timestamp1', timestamp1)
      const findQ1 = childMitData.filter(
        (d) =>
          timestamp1 >= parseInt(d.start_date) &&
          timestamp1 <= parseInt(d.end_date)
      )
      if (findQ1.length) {
        return findQ1[0].quarter_name
      }
    }
  }
  return null
}

const isAnnualMITQuarter = (quarterType = null) =>
  quarterType == 2 || quarterType == 3 || quarterType == 4

const getQuarterTooltip = (op = {}, time, seriesName = null, qLabels = []) => {
  const { childMitData, isChildMit, series, upd_data_interval, end_date, quarter_target_type } = op
  const xDateFormat =
    upd_data_interval == krdetailConstants.INTERVAL_DAY ||
    isWeeklyInterval(upd_data_interval)
      ? '%b %e'
      : '%b'
  if (isChildMit) {
    if (seriesName === 'Target') {
      return `${Highcharts.dateFormat('%b %e', parseInt(end_date) * 1000)}`
    }
    return `${Highcharts.dateFormat(xDateFormat, time)}`
  }
  let timestamp = time
  if (upd_data_interval == krdetailConstants.INTERVAL_QUARTER) {
    if (seriesName === 'Actual' ) {
      if (qLabels[time]) {
        if(quarter_target_type == 1)
          return `${Highcharts.dateFormat(xDateFormat, parseInt(timestamp))}`
        else
          return qLabels[time]
      }

      const l = findQuarterLabel(op, time)
      if (l != null) {
        return l
      }
    }
  }
  const timestamp1 = timestamp / 1000
  if (Array.isArray(childMitData) && childMitData.length) {
    const findQ = childMitData.filter(
      (d) =>
        timestamp >= Number(moment.unix(d.start_date).valueOf()) &&
        timestamp <= Number(moment.unix(d.end_date).endOf('day').valueOf())
    )
    let krObj = {}
    if (findQ.length) {
      krObj = { ...findQ[0] }
    } else {
      const childKRLength = childMitData.length
      const firstKR = childMitData[0]
      const lastKR = childMitData[childKRLength - 1]
      if (firstKR && timestamp1 < parseInt(firstKR.start_date)) {
        krObj = firstKR
      }
      if (
        !Object.keys(krObj).length &&
        lastKR &&
        timestamp1 > parseInt(lastKR.end_date)
      ) {
        krObj = lastKR
      }
    }
    if (seriesName === 'Actual') {
      if (
        upd_data_interval == krdetailConstants.INTERVAL_QUARTER &&
        Object.keys(krObj).length
      ) {
        return krObj.quarter_name
      } else if (upd_data_interval == krdetailConstants.INTERVAL_MONTH) {
        const endofmonth = moment.utc(timestamp).endOf('month').valueOf()
        return `${Highcharts.dateFormat(xDateFormat, endofmonth)}`
      }
      return `${Highcharts.dateFormat(xDateFormat, parseInt(timestamp))}`
    } else if (seriesName === 'Target') {
      if (Object.keys(krObj).length) {
        return `${Highcharts.dateFormat(
          '%b %e',
          parseInt(krObj.end_date) * 1000
        )}`
      }
    }
    return `${Highcharts.dateFormat(xDateFormat, parseInt(timestamp))}`
  }
  return ''
}

/**
 * generate ticks at regular intervals (from beginning to end of series range) to:
 *  1. reduce tick density (maximum of 27 ticks)
 *  2. plot ticks evenly (quarterly ticks will have gaps)
 *
 * @param {*} params
 * @returns
 */
const generateTicks = (params) => {
  const maxTicks = 27;
  const { customSeries = [], series = [], seriesEnd, interval, tickInterval } = params ?? {};
  const hasCumulative = customSeries?.[0]?.data?.slice(-1)[0]?.iscumulative;
  const dataTicks = series?.[0]?.processedXData.map((d) => {
    const date = moment.utc(d);

    return date.isValid() ? date._d.getTime() : d; // all should be valid dates, but the last "Cumulative" data point
  });
  const start = dataTicks?.[0];
  // get the last valid data point
  const end = seriesEnd ?? dataTicks?.[dataTicks?.length - (hasCumulative ? 2 : 1)];
  if(!start || !end) return;

  // quarter ticks much match exactly (for labels), also quarters never have a high density
  if(interval == krdetailConstants.INTERVAL_QUARTER) return dataTicks;

  let curr = start;
  const ticks = [];

  if(curr == end) {
    ticks.push(curr);
  } else {
    while (curr < end) {
      ticks.push(curr);

      // reduce density of daily ticks, plot maximum of 27 ticks
      if(interval == krdetailConstants.INTERVAL_DAY && dataTicks.length > maxTicks) {
        const relativeTickInterval = (end - start) / maxTicks
        curr += relativeTickInterval;
      }
      // reduce density of weekly ticks, plot tick every two weeks
      else if(interval == krdetailConstants.INTERVAL_WEEK && dataTicks.length > maxTicks) {
        curr += tickInterval * 2;
      }
      // month interval is not static, must be calculated
      else if (interval == krdetailConstants.INTERVAL_MONTH) {
        curr = moment.utc(curr).add(1, 'M').endOf('month').valueOf()
      }
      // plot tick at regular interval
      else {
        curr += tickInterval;
      }
    }
    // end with the last data point to ensure all data is visible
    if(end > ticks[ticks.length - 1]) ticks.push(end);
  }

  // add tick for cumulative
  if(hasCumulative) ticks.push(dataTicks[dataTicks.length - 1]);

  return ticks;
}

const drawLineCharts = (
  renderId,
  chartOptions,
  range = {},
  showLegends = false,
  isChildMit,
  pageSource
) => {
  const interval = chartOptions.upd_data_interval
  const height = 233
  const isWeekInterval =
    interval == krdetailConstants.INTERVAL_DAY || isWeeklyInterval(interval)
  const xDateFormat = isWeekInterval ? '%b %e' : '%b'
  const labelRotation = isWeekInterval ? -45 : 0
  const seriesData = constructSeries(chartOptions, range, true, []) || []
  let seriesDataJson = JSON.parse(JSON.stringify(seriesData))

  const tickInterval = getTickInterval(interval)
  let seriesStart = null
  let maxY = null
  let minX = moment.utc(chartOptions.start_date * 1000)._d.getTime()

  let maxX =
    typeof chartOptions.end_date !== 'undefined' &&
    chartOptions.end_date != null
      ? moment.utc(chartOptions.end_date * 1000)._d.getTime()
      : null

  let targetX = maxX
  let xAxisLables = []
  let upperTarget = null;
  const quarterLables = []
  if (seriesDataJson.length > 0) {
    // only applies when date range is selected manually

    seriesDataJson = seriesDataJson.map((series, i) => {
      if (series.data && series.data.length > 0 && i === 0) {
        if (
          typeof range.end !== 'undefined' &&
          typeof range.start !== 'undefined'
        ) {
          const rangeStart = moment.utc(range.start)._d.getTime()
          const rangeEnd = moment.utc(range.end)._d.getTime()

          // const isTooCloseToRangeStart = (value) => Math.abs(value[0] - rangeStart) <= tickInterval;
          // const isTooCloseToRangeEnd = (value) => Math.abs(value[0] - rangeEnd) <= tickInterval && value[0] <= rangeEnd;

          // minX = Math.min(...series.data.filter(isTooCloseToRangeStart).map(j => j[0]));
          // maxX = Math.max(...series.data.filter(isTooCloseToRangeEnd).map(j => j[0]));
        }
        // series.data = series.data.filter( point => {
        //   quarterLables[point[0]] = (typeof point[3] !== 'undefined') ? point[3] : null;
        //   if (point[1] && (point[1] > maxY || (!maxY && point[1]))) {
        //     maxY = Number(point[1]);
        //   }
        //   return point[0] > seriesStart && point[0] >= minX && point[0] <= maxX;
        // })
        series.data.forEach((point) => {
          quarterLables[point[0]] =
            typeof point[3] !== 'undefined' ? point[3] : null
          if (point[1] && (point[1] > maxY || (!maxY && point[1]))) {
            maxY = Number(point[1])
          }
          //return point[0] > seriesStart && point[0] >= minX && point[0] <= maxX;
        })
        xAxisLables = [].concat(
          ...xAxisLables,
          ...series.data.map((dd) => dd[0])
        )
      }
      if (chartOptions.counting == krdetailConstants.METRIC_COUNTING_RANGE) {
        upperTarget = series.upper_limit
      }
      return series
    })

    xAxisLables = [...new Set(xAxisLables)]

    minX = xAxisLables.length ? Math.min(...xAxisLables) : null
    maxX = xAxisLables.length ? Math.max(maxX, Math.max(...xAxisLables)) : null
    //if(!isChildMit){
    if (
      seriesDataJson[1] &&
      seriesDataJson[1].data &&
      seriesDataJson[1].data[1] &&
      seriesDataJson[1].data[1].y &&
      seriesDataJson[1].data[1].y > maxY
    ) {
      maxY = seriesDataJson[1].data[1].y
    }
    //}
  }

  let tickers = [] // need this to access ticks in the formatter()
  let maxVal = null
  let thresholdArray = []
  if (chartOptions.target && chartOptions.target != '') {
    let thersholdValue = chartOptions.target;
    if (chartOptions.source == krdetailConstants.SOURCE_WORKSTREAM &&
      chartOptions.metric == krdetailConstants.METRIC_PERCENTAGE) {
      thersholdValue = 100 ;
      maxVal =100;
    }
    if (chartOptions.counting == krdetailConstants.METRIC_COUNTING_RANGE && upperTarget != null
      && chartOptions.achieve_target <= upperTarget) {
      thersholdValue = upperTarget;
      maxVal = upperTarget;
    }
    let arr = {
      color: '#27B8B8',
      width: '1.5',
      value: thersholdValue,
      dashStyle: 'Solid',
    }
    thresholdArray.push(arr)
    if (chartOptions.isStretchEnabled && chartOptions.stretch != '') {
      let arr = {
        color: '#1D8734',
        width: '1.5',
        value: chartOptions.stretch,
        dashStyle: 'Solid',
      }
      thresholdArray.push(arr)
    }
    if ('target' in chartOptions && chartOptions.target !== undefined) {
      if (chartOptions.isStretchEnabled && 'stretch' in chartOptions && chartOptions.stretch != '' && (chartOptions.counting == 9 || chartOptions.counting == 1)) {
        maxVal = maxVal ?? (maxY
          ? Math.max(+chartOptions.stretch, +maxY)
          : +chartOptions.stretch)
      } else {
        maxVal = maxVal ?? (maxY
          ? Math.max(+chartOptions.target, +maxY)
          : +chartOptions.target)
      }
    }
  }

  const units = getIntervalUnits(interval)

  const orgConfigPeriod = chartOptions.orgConfigPeriod || false
  const isAnnualMitKR = isAnnualMITQuarter(chartOptions.quarter_target_type)

  let legendsConfig = {
    enabled: false,
  }
  if (showLegends) {
    legendsConfig = {
      enabled: true,
      align: 'left',
      verticalAlign: 'top',
      x: -20,
      y: pageSource ? -130 : -78,
      itemStyle: {
        fontWeight: 'normal',
        fontSize: '12px',
        fontFamily: 'Lato',
        color: '#596373',
      },
      itemHoverStyle: {
        cursor: 'default',
      },
    }
  }
  if (
    showLegends &&
    (interval == krdetailConstants.INTERVAL_DAY ||
      interval == krdetailConstants.INTERVAL_QUARTER ||
      interval == krdetailConstants.INTERVAL_MONTH ||
      interval == krdetailConstants.INTERVAL_WEEK ||
      interval == krdetailConstants.INTERVAL_BIWEEKLY)
  ) {
    maxVal = null
    thresholdArray = []
  }
  let krIndex = 0
  const options = {
    chart: {
      renderTo: renderId,
      className: 'kr-det-page-line-charts ',
      type: 'column',
      plotBackgroundColor: '#F6F7F9',
      plotBorderWidth: 1,
      plotBorderColor: '#eceff2',
      height,
      zoomType: 'x',
      resetZoomButton: {
        position: {
          x: -15,
          y: 15,
        },
        relativeTo: 'chart',
      },
      events: {
        load() {
          $(`#${renderId} .highcharts-container`).css('overflow', 'visible')
          $(`#${renderId} .highcharts-container .highcharts-root`).css(
            'overflow',
            'visible'
          )
          $(
            `#${renderId} .highcharts-container .highcharts-xaxis-labels span:last-child`
          ).css({
            overflow: 'visible',
            'white-space': 'nowrap',
          })
          $(`#${renderId}`).data('range', {
            start: this.xAxis[0].dataMin,
            end: this.xAxis[0].dataMax,
          })
          if (
            typeof range.start === 'undefined' &&
            typeof range.end === 'undefined'
          ) {
            minX = this.xAxis[0].dataMin
            maxX = this.xAxis[0].dataMax
          }
          if (minX !== undefined && minX != null) {
            let dateText = moment.utc(minX).format('MMM DD, YYYY')
            if (maxX !== 'undefined' && maxX != null) {
              const endDateText = moment.utc(maxX).format('MMM DD, YYYY')
              dateText = `${dateText} - ${endDateText}`
            }
          }
        },
      },
    },
    title: {
      text: '',
    },
    lang: {
      accessibility: {
        chartContainerLabel:
          chartOptions['titleText'] +
          '. Use focus, form, or scan mode to navigate each data point on the chart with your left and right arrow keys. Use up and down arrows to switch between multi-line data points.',
      },
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
      showTable: chartOptions['displayOptions']['table'],
      csv: {
        // Uncomment for custom column header formatter.
        // This function is called for each column header.
        columnHeaderFormatter: function (item, key) {
          if (!item || item instanceof Highcharts.Axis) {
            return i18next.t('Date')
          } else {
            return item.name
          }
        },
        //
        dateFormat: '%b %e, %Y',
      },
    },
    legend: legendsConfig,
    xAxis: {
      ordinal: false,
      type: 'datetime',
      startOnTick: true, // (interval == krdetailConstants.INTERVAL_WEEK) ? true : false,
      endOnTick: true,
      tickLength: 5,
      tickWidth: 1,
      tickInterval: tickInterval,
      maxPadding: 0.2,
      min: minX,

      tickPositioner: function tickPositioner() {
        const seriesEnd = !showLegends && interval != krdetailConstants.INTERVAL_QUARTER && seriesDataJson.length > 1 ? this.series?.[1]?.processedXData?.[1] : null
        const ticks = generateTicks({ customSeries: seriesDataJson, series: this.series, seriesEnd, interval, tickInterval })

        if (ticks?.length) {
          tickers = [...ticks] // need this to access ticks in the formatter()
          if (interval == krdetailConstants.INTERVAL_WEEK && minX > ticks[0]) {
            ticks.shift()
          }
          ticks.info = this.series[0].currentDataGrouping
          this.min = ticks[0]
          this.dataMin = this.min
          return ticks
        }
      },

      labels: {
        // useHTML: true,
        crop: true,
        style: {
          fontSize: '10px',
          color: '#666',
          textOverflow: 'none',
        },

        /**
         * Formats a tick label
         *
         * @return {string} label string
         */
        formatter() {
          const series = this.chart.series[0].processedXData
          const stepThreshold = 30 // if >30 datapoints we won't show all of them
          let showEvery = 1 // show each label
          const label = this
          // if(typeof this.value == "number"){
          //   if (chartOptions.upd_data_interval == krdetailConstants.INTERVAL_QUARTER && showLegends) {
          //     if (isAnnualMitKR || (typeof chartOptions.isChildAnnualMit !== undefined && chartOptions.isChildAnnualMit)) {
          //       const qLabel = quarterLables[this.value] || null;
          //       // if (chartOptions.data_interval == krdetailConstants.INTERVAL_QUARTER && qLabel != null && qLabel) {
          //       //   return qLabel;
          //       // }
          //       console.log(qLabel)
          //       if (qLabel != null && qLabel) {
          //         return qLabel;
          //       }
          //       const l = findQuarterLabel(chartOptions, this.value);
          //       console.log(l)
          //       console.log(l);
          //       if (l != null) {
          //         return l;
          //       }
          //     }
          //     console.log('indside')
          //     return Highcharts.dateFormat(xDateFormat, this.value);
          //   }
          //   console.log('outside')
          //   const xTimestamp = this.value;
          //   const labelTime = (chartOptions.upd_data_interval == krdetailConstants.INTERVAL_QUARTER && !orgConfigPeriod) ? moment.utc(xTimestamp).add(2, 'months').valueOf() : moment(xTimestamp).utc()._d.getTime();
          //   return Highcharts.dateFormat(xDateFormat, labelTime);
          // } else{
          //   return this.value;
          // }
          if (
            showLegends &&
            chartOptions.upd_data_interval == krdetailConstants.INTERVAL_QUARTER
          ) {

            if (
              isAnnualMitKR ||
              (typeof chartOptions.isChildAnnualMit !== undefined &&
                chartOptions.isChildAnnualMit)
            ) {
              const qLabel = quarterLables[this.value] || null
              // if (chartOptions.upd_data_interval == krdetailConstants.INTERVAL_QUARTER && qLabel != null && qLabel) {
              //   return qLabel;
              // }
              //console.log('l', this.value)

              if (qLabel != null && qLabel) {
                return qLabel
              }
              const l = findQuarterLabel(chartOptions, this.value)

              if (l != null) {

                return l
              }
            }
            return Highcharts.dateFormat(xDateFormat, this.value)
          }
          const labelTime = moment(label.value).utc()._d.getTime()
          const labelValue = Highcharts.dateFormat(xDateFormat, labelTime)
          const currentPos = series.findIndex((v) => v === label.value)
          // if (series.length > stepThreshold * 6) {
          //   showEvery = 6;
          // } else if (series.length > stepThreshold * 3) {
          //   showEvery = 4;
          // } else if (series.length > stepThreshold) {
          //   showEvery = 3;
          // }

          const currentPoint = this.chart.series[0].processedXData[currentPos]
          const theEndPoint = Math.min(maxX, targetX)
          // const closerThanHalfIntevalToTheEnd = Math.abs(currentPoint - theEndPoint) < tickInterval * showEvery / 2;
          // const furtherThanEnd = currentPoint > theEndPoint  ? true : false;

          // if (series.length > stepThreshold && !furtherThanEnd &&
          //    ((currentPos % showEvery === 0 && !closerThanHalfIntevalToTheEnd) || label.isFirst || label.isLast)) {
          //     return labelValue;
          // }

          // if (series.length <= stepThreshold && (!closerThanHalfIntevalToTheEnd || label.isLast) && !furtherThanEnd) {
          //     return labelValue;
          // }

          return labelValue
        },
      },
      // labels: {
      //   formatter:  function() {
      //     // var quarter = Math.ceil((new Date(this.value).getMonth() + 1) / 3);
      //     //return Highcharts.dateFormat('Q' + quarter + ' %Y' , this.value);
      //     let count = 0;

      //     return `Q${this.value+1}`;
      //   }
      // },
      lineColor: '#eceff2',
      gridLineColor: '#eceff2',
    },
    yAxis: {
      visible: SHOW_Y_AXIS,
      plotLines: thresholdArray,
      title: null,
      tickWidth: 0,
      tickLength: 5,
      tickColor: '#eceff2',
      max: maxVal,
      labels: {
        style: {
          fontSize: '10px',
          color: '#666',
        },
        formatter:  function() {
          let symbol = chartOptions.metric == krdetailConstants.METRIC_PERCENTAGE ? '%' : '';
          return `${formatNumber(this.value) + symbol}`;
        },
      },
      lineColor: '#eceff2',
      gridLineColor: '#eceff2',
    },
    plotOptions: {
      series: {
        connectNulls: true,
        color: '#3BC1E5',
        marker: {
          // enabled: true
        },
        fillColor: {
          linearGradient: [0, 0, 0, 225],
          stops: [
            ['0', 'rgba(59,193,229,0.8)'],
            ['1', 'rgba(59,193,229,0)'],
          ],
        },
        // dataGrouping: {
        //   forced: true,
        //   enabled: chartOptions.upd_data_interval != chartOptions.data_interval ? true : false,
        //   units: units,
        //   approximation: 'close'
        // },
        events: {
          legendItemClick: function () {
            return false
          },
        },
        states: {
          inactive: {
            opacity: 0.7,
          },
        },
      },
    },
    series: seriesDataJson || [],
    rangeSelector: {
      enabled: false,
    },
    scrollbar: {
      enabled: false,
    },
    tooltip: {
      //shared: (showLegends) ? true: false,
      enabled: true,
      useHTML: true,
      //outside: true,
      borderRadius: showLegends ? 8 : 5,
      shadow: false,
      backgroundColor: 'rgba(255,255,255,1)',
      // positioner: function (labelWidth, labelHeight, point) {
      //   if(showLegends){
      //     return {
      //       x: point.plotX - 12,
      //       y: point.plotY - 30
      //     };
      //   }
      //   return {
      //     x: point.plotX,
      //     y: point.plotY
      //   }
      // },
      formatter() {
        const labelTime = moment(this.x).utc()._d.getTime()
        let date = Highcharts.dateFormat(xDateFormat, labelTime)
        const value = formatNumber(this.y)
        let xVal = this.x / 1000
        let comment = chartOptions.showAttachment
          ? typeof chartOptions.tooltip[xVal] !== undefined
            ? chartOptions.tooltip[xVal]
            : ''
          : ''
        comment = (typeof comment !== 'undefined') ? comment : ''
        let width = getToolTipWidth(comment);
        let tooltip = `<div style='min-width:${width}; max-height: 150px; overflow-y: auto;' class='krDetailsGraphFont' data-testid="krDetailsGraphFont">${
          value ? formatNumber(value) : 0
        } <br/> ${date}<br/>${comment}</div>`
        if (showLegends) {
          date = getQuarterTooltip(
            chartOptions,
            this.x,
            this.series.name,
            quarterLables
          )
          comment =
            comment.length > 0
              ? `<span style='display:block;border-top:1px solid #C4CBD7;margin-top:8px;padding-top:8px'>${comment}</span>`
              : ''
          width = comment.length > 100 ? '165px' : '110px'
          tooltip = `<div style='min-width:${width};padding: 4px 4px;background-color:#fff; max-height: 150px; overflow-y: auto;' class='krDetailsGraphFont'>
                      ${date} ${this.series.name} : ${
            value ? formatNumber(value) : 0
          } <br/>
                      ${comment}</div>`
        }
        return tooltip
      },
    },
  }

  const myChart = new Highcharts.Chart(options)
}

const drawBarCharts = (
  renderId,
  chartOptions,
  range = {},
  surveyUserSelected = '',
  pageSource = ''
) => {
  // interval: 1 daily, 2 weekly, 3 monthly, 4 quarterly
  const interval = chartOptions.upd_data_interval
  const xDateFormat =
    interval == krdetailConstants.INTERVAL_DAY ||
    isWeeklyInterval(interval) ||
    interval == krdetailConstants.INTERVAL_ONCE
      ? '%b %e'
      : '%b'
  const tickInterval = getTickInterval(interval)


  const height = 233
  let groupPadding = 0.2
  let pointWidth = 10 // default bar width
  const seriesData = chartOptions.series || []
  const seriesLength = seriesData.length // how many data group we have
  let seriesDataJson = JSON.parse(JSON.stringify(seriesData))
  let isExceedSeriesDataLimit = false

  let minX =
    typeof range.start !== 'undefined'
      ? moment.utc(range.start)._d.getTime()
      : typeof chartOptions.start_date !== 'undefined' &&
        chartOptions.start_date != null
      ? moment.utc(chartOptions.start_date * 1000)._d.getTime()
      : null

  let maxX =
    typeof range.end !== 'undefined'
      ? moment.utc(range.end)._d.getTime()
      : typeof chartOptions.end_date !== 'undefined' &&
        chartOptions.end_date != null
      ? moment.utc(chartOptions.end_date * 1000)._d.getTime()
      : null

  let xAxisLables = []
  let isRangeDate = false
  let seriesDataCount = 0
  if (typeof range.start !== 'undefined') {
    isRangeDate = true
  }

  const units = getIntervalUnits(interval)

  let childMITKRCount = 0
  if (Array.isArray(seriesDataJson)) {
    const seriesDataLimit = 30
    if (isRangeDate) {
      seriesDataJson = seriesDataJson.map((s) => {
        if (typeof s.childMIT !== 'undefined' && s.childMIT == true) {
          childMITKRCount++
        }
        if (typeof s.data !== 'undefined' && Array.isArray(s.data)) {
          s.data = s.data.filter((fd) => fd[0] >= minX && fd[0] <= maxX)
          /* if data count is over 30, show the latest 30 data points */
          if (!isRangeDate && s.data.length > seriesDataLimit) {
            isExceedSeriesDataLimit = true
            s.data = s.data.splice(-seriesDataLimit)
          }
          seriesDataCount = seriesDataCount + s.data.length
          xAxisLables = [].concat(...xAxisLables, ...s.data.map((dd) => dd[0]))
        }
        return s
      })
      xAxisLables = [...new Set(xAxisLables)]
      minX = xAxisLables.length ? Math.min(...xAxisLables) : null
      maxX = xAxisLables.length ? Math.max(...xAxisLables) : null
    }
    if (xAxisLables.length) {
      const seriesDataLength = xAxisLables.length // how many data point on first data group
      const totalElementCount = seriesDataCount
      if (seriesLength == 1) {
        groupPadding = 0.47
      } else if (seriesLength == 2) {
        if (totalElementCount <= 2) {
          groupPadding = 0.48
        } else if (totalElementCount > 2 && totalElementCount <= 4) {
          groupPadding = 0.45
        } else if (totalElementCount > 4 && totalElementCount <= 6) {
          groupPadding = 0.43
        } else if (totalElementCount > 6 && totalElementCount <= 10) {
          groupPadding = 0.41
        } else if (totalElementCount > 10 && totalElementCount <= 14) {
          groupPadding = 0.38
        } else if (totalElementCount > 14 && totalElementCount <= 30) {
          groupPadding = 0.32
        } else if (totalElementCount > 30 && totalElementCount <= 44) {
          pointWidth = 5
          groupPadding = 0.35
        } else if (totalElementCount > 44 && totalElementCount <= 60) {
          pointWidth = 4
          groupPadding = 0.3
        }
      } else if (seriesLength == 3) {
        if (totalElementCount <= 6) {
          groupPadding = totalElementCount <= 3 ? 0.47 : 0.45
        } else if (totalElementCount > 6 && totalElementCount <= 14) {
          groupPadding = 0.41
        } else if (totalElementCount > 14 && totalElementCount <= 24) {
          groupPadding = 0.38
        } else if (totalElementCount > 24 && totalElementCount <= 44) {
          pointWidth = 5
          groupPadding = 0.35
        } else if (totalElementCount > 44 && totalElementCount <= 60) {
          pointWidth = 4
          groupPadding = 0.3
        }
      } else if (seriesLength == 4) {
        if (totalElementCount <= 4) {
          groupPadding = 0.46
        } else if (totalElementCount > 4 && totalElementCount <= 12) {
          groupPadding = 0.41
        } else if (totalElementCount > 12 && totalElementCount <= 30) {
          groupPadding = 0.38
        } else if (totalElementCount > 30 && totalElementCount <= 44) {
          pointWidth = 5
          groupPadding = 0.35
        } else if (totalElementCount > 44 && totalElementCount <= 60) {
          pointWidth = 4
          groupPadding = 0.3
        }
      } else if (seriesLength == 5) {
        pointWidth = 5
        if (totalElementCount <= 5) {
          groupPadding = 0.45
        } else if (totalElementCount > 5 && totalElementCount <= 10) {
          groupPadding = 0.44
        } else if (totalElementCount > 10 && totalElementCount <= 30) {
          groupPadding = 0.38
        } else if (totalElementCount > 30 && totalElementCount <= 40) {
          pointWidth = 5
          groupPadding = 0.35
        } else if (totalElementCount > 30 && totalElementCount <= 40) {
          pointWidth = 5
          groupPadding = 0.3
        } else if (totalElementCount > 40 && totalElementCount <= 60) {
          pointWidth = 5
          groupPadding = 0.28
        }
      } else if (seriesLength == 6) {
        pointWidth = 5
        if (totalElementCount <= 12) {
          groupPadding = totalElementCount <= 6 ? 0.45 : 0.41
        } else if (totalElementCount > 12 && totalElementCount <= 24) {
          groupPadding = totalElementCount <= 18 ? 0.35 : 0.3
        } else if (totalElementCount > 24 && totalElementCount <= 30) {
          groupPadding = 0.3
        } else if (totalElementCount > 30 && totalElementCount <= 44) {
          pointWidth = 5
          groupPadding = 0.28
        } else if (totalElementCount > 44 && totalElementCount <= 60) {
          pointWidth = 5
          groupPadding = 0.25
        }
      } else if (seriesLength == 7 || seriesLength === 8) {
        // for group over 3, we don't need any space between column, not enough space on the page
        groupPadding = 0
        pointWidth = 5
        if (totalElementCount <= 7) {
          groupPadding = 0.45
        } else if (totalElementCount > 7 && totalElementCount <= 14) {
          groupPadding = 0.41
        } else if (totalElementCount > 14 && totalElementCount <= 30) {
          groupPadding = 0.39
        } else if (totalElementCount > 30 && totalElementCount <= 45) {
          pointWidth = 4
          groupPadding = 0.3
        } else if (totalElementCount > 45 && totalElementCount <= 60) {
          pointWidth = 4
          groupPadding = 0.25
        }
      } else {
        groupPadding = 0
        pointWidth = 4
      }

      if (seriesLength == childMITKRCount) {
        pointWidth = 10
        groupPadding = 0.5
        if (totalElementCount > 30 && totalElementCount <= 45) {
          pointWidth = 4
          groupPadding = 0.3
        } else if (totalElementCount > 45 && totalElementCount <= 60) {
          pointWidth = 4
          groupPadding = 0.25
        }
      }
      if (totalElementCount > 60) {
        pointWidth = 4
        groupPadding = 0
      }
    }
  }

  let columnPlotOptions = {
    grouping: true,
    pointWidth,
    groupPadding,
    pointPadding: 0,
    borderWidth: 0,
    stacking: 'normal',
  }
  if (chartOptions.source == krdetailConstants.SOURCE_SURVEY) {
    columnPlotOptions['negativeColor'] = '#142D4D'
  }
  const labelRotation = interval == 1 || interval == 2 ? -45 : 0
  const orgConfigPeriod = chartOptions.orgConfigPeriod || false

  const options = {
    chart: {
      renderTo: renderId,
      className: 'kr-det-page-bar-charts ',
      type: 'column',
      plotBackgroundColor: '#F6F7F9',
      plotBorderWidth: 1,
      plotBorderColor: '#eceff2',
      height,
      zoomType: 'x',
      resetZoomButton: {
        position: {
          x: -15,
          y: 15,
        },
        relativeTo: 'chart',
      },
      events: {
        load() {
          $(`#${renderId}`).data('range', {
            start: this.xAxis[0].dataMin,
            end: this.xAxis[0].dataMax,
          })
          if (
            typeof range.start === 'undefined' &&
            typeof range.end === 'undefined'
          ) {
            minX = this.xAxis[0].dataMin
            maxX = this.xAxis[0].dataMax
          }
          if (minX !== undefined && minX != null) {
            let dateText = moment.utc(minX).format('MMM DD, YYYY')
            if (maxX !== 'undefined' && maxX != null) {
              const endDateText = moment.utc(maxX).format('MMM DD, YYYY')
              dateText = `${dateText} - ${endDateText}`
            }
            const renderCalId = $(`#${renderId}`)
              .parents('.kr-det-graph-div')
              .find('.daterangecomp-text-wrapper')
            //$(renderCalId).find('input').val(dateText);
            //$(renderCalId).find('button').text(dateText);
          }
        },
      },
    },
    title: {
      text: '',
    },
    lang: {
      accessibility: {
        chartContainerLabel:
          chartOptions['titleText'] +
          '. Use focus, form, or scan mode to navigate each data point on the chart with your left and right arrow keys. Use up and down arrows to switch between multi-line data points.',
      },
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
      showTable: chartOptions['displayOptions']['table'],
      csv: {
        // Uncomment for custom column header formatter.
        // This function is called for each column header.
        columnHeaderFormatter: function (item, key) {
          if (!item || item instanceof Highcharts.Axis) {
            return i18next.t('Date')
          } else {
            return item.name
          }
        },
        //
        dateFormat: '%b %e, %Y',
      },
    },
    legend: {
      enabled: false,
    },
    xAxis: {
      type: 'datetime',
      tickLength: 5,
      tickWidth: 1,
      // tickInterval: tickInterval,
      // min: minX,
      tickPositioner: function tickPositioner() {
        const ticks = generateTicks({ customSeries: seriesDataJson, series: this.series, interval, tickInterval })

        if (ticks?.length) {
          const xDataLength = ticks.length

          const {
            upd_data_interval,
            isChildMit,
            end_date,
            data_interval,
            orgPeriods,
          } = chartOptions
          ticks.info = this.series[0].currentDataGrouping
          if (interval == krdetailConstants.INTERVAL_WEEK && minX > ticks[0]) {
            ticks.shift()
          }
          this.min = ticks[0]
          return ticks
        }
      },
      labels: {
        style: {
          fontSize: '10px',
          color: '#666',
        },
        rotation: labelRotation,
        formatter() {
          const labelTime = moment(this.value).utc()._d.getTime()
          const displayLabel = Highcharts.dateFormat(xDateFormat, labelTime)
          return displayLabel
        },
      },
      lineColor: '#eceff2',
      gridLineColor: '#eceff2',
    },
    yAxis: {
      visible: SHOW_Y_AXIS,
      title: null,
      tickWidth: 0,
      tickLength: 5,
      tickColor: '#eceff2',
      labels: {
        align: 'center',
        style: {
          fontSize: '10px',
          color: '#666',
        },
        formatter:  function() {
          let symbol = chartOptions.metric == krdetailConstants.METRIC_PERCENTAGE ? '%' : '';
          return `${formatNumber(this.value) + symbol}`;
        },
        enabled:
          surveyUserSelected != '' &&
          chartOptions.counting ==
            krdetailConstants.METRIC_COUNTING_SURVEY_YES_NO
            ? false
            : true,
      },
      lineColor: '#eceff2',
      gridLineColor: '#eceff2',
    },
    plotOptions: {
      column: columnPlotOptions,
      series: {
        connectNulls: true,
        dataGrouping: {
          forced: true,
          enabled:
            chartOptions.upd_data_interval != chartOptions.data_interval
              ? true
              : false,
          units: units,
        },
        states: {
          inactive: {
            opacity: 0.7,
          },
        },
      },
    },
    series: seriesDataJson || [],
    rangeSelector: {
      enabled: false,
    },
    scrollbar: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      useHTML: true,
      borderRadius: 5,
      shadow: false,
      backgroundColor: 'rgba(255,255,255,1)',
      formatter() {
        const labelTime = moment(this.x).utc()._d.getTime()
        const date = Highcharts.dateFormat(xDateFormat, labelTime)
        const value =
          surveyUserSelected != '' &&
          chartOptions.counting ==
            krdetailConstants.METRIC_COUNTING_SURVEY_YES_NO
            ? this.y == 10
              ? 'Yes'
              : 'No'
            : this.y
        let xVal = this.x / 1000
        let comment = chartOptions.showAttachment
          ? chartOptions.tooltip[xVal]
          : ''
        comment = typeof comment !== 'undefined' ? comment : ''
        let width = comment.length > 100 ? '200px' : 'fit-content'
        return `<div style='min-width:${width};' class='krDetailsGraphFont'>${
          value && Number.isInteger(value) ? formatNumber(value) : value
        } <br/> ${date} <br /> ${comment}</div>`
      },
    },
  }

  const myChart = new Highcharts.Chart(options)
}

const drawStackedBarCharts = (renderId, chartOptions, range = {}) => {
  // interval: 1 daily, 2 weekly, 3 monthly, 4 quarterly
  const interval = chartOptions.upd_data_interval
  const xDateFormat =
    interval == krdetailConstants.INTERVAL_DAY || isWeeklyInterval(interval)
      ? '%b %e'
      : '%b'
  const height = 233
  let groupPadding = 0.2
  let pointWidth = 10 // default bar width
  const seriesData = chartOptions.series || []
  const seriesLength = seriesData.length // how many data group we have
  let seriesDataJson = JSON.parse(JSON.stringify(seriesData))
  let isExceedSeriesDataLimit = false

  let tickInterval = getTickInterval(interval)

  let minX =
    typeof range.start !== 'undefined'
      ? moment.utc(range.start)._d.getTime()
      : typeof chartOptions.start_date !== 'undefined' &&
        chartOptions.start_date != null
      ? moment.utc(chartOptions.start_date * 1000)._d.getTime()
      : null

  let maxX =
    typeof range.end !== 'undefined'
      ? moment.utc(range.end)._d.getTime()
      : typeof chartOptions.end_date !== 'undefined' &&
        chartOptions.end_date != null
      ? moment.utc(chartOptions.end_date * 1000)._d.getTime()
      : null

  let xAxisLables = []
  let isRangeDate = false
  let seriesDataCount = 0
  if (typeof range.start !== 'undefined') {
    isRangeDate = true
  }

  const units = getIntervalUnits(interval)

  let childMITKRCount = 0
  if (Array.isArray(seriesDataJson)) {
    const seriesDataLimit = 30
    if (isRangeDate) {
      seriesDataJson = seriesDataJson.map((s) => {
        if (typeof s.childMIT !== 'undefined' && s.childMIT == true) {
          childMITKRCount++
        }
        if (typeof s.data !== 'undefined' && Array.isArray(s.data)) {
          s.data = s.data.filter((fd) => fd[0] >= minX && fd[0] <= maxX)
          /* if data count is over 30, show the latest 30 data points */
          if (!isRangeDate && s.data.length > seriesDataLimit) {
            isExceedSeriesDataLimit = true
            s.data = s.data.splice(-seriesDataLimit)
          }
          seriesDataCount = seriesDataCount + s.data.length
          xAxisLables = [].concat(...xAxisLables, ...s.data.map((dd) => dd[0]))
        }
        return s
      })
      xAxisLables = [...new Set(xAxisLables)]
      minX = xAxisLables.length ? Math.min(...xAxisLables) : null
      maxX = xAxisLables.length ? Math.max(...xAxisLables) : null
    }

    if (xAxisLables.length) {
      const totalElementCount = seriesDataCount
      if (seriesLength == 1) {
        groupPadding = 0.47
      } else if (seriesLength == 2) {
        if (totalElementCount <= 2) {
          groupPadding = 0.48
        } else if (totalElementCount > 2 && totalElementCount <= 4) {
          groupPadding = 0.45
        } else if (totalElementCount > 4 && totalElementCount <= 6) {
          groupPadding = 0.43
        } else if (totalElementCount > 6 && totalElementCount <= 10) {
          groupPadding = 0.41
        } else if (totalElementCount > 10 && totalElementCount <= 14) {
          groupPadding = 0.38
        } else if (totalElementCount > 14 && totalElementCount <= 30) {
          groupPadding = 0.32
        } else if (totalElementCount > 30 && totalElementCount <= 44) {
          pointWidth = 5
          groupPadding = 0.35
        } else if (totalElementCount > 44 && totalElementCount <= 60) {
          pointWidth = 4
          groupPadding = 0.3
        }
      } else if (seriesLength == 3) {
        if (totalElementCount <= 6) {
          groupPadding = totalElementCount <= 3 ? 0.47 : 0.45
        } else if (totalElementCount > 6 && totalElementCount <= 14) {
          groupPadding = 0.41
        } else if (totalElementCount > 14 && totalElementCount <= 24) {
          groupPadding = 0.38
        } else if (totalElementCount > 24 && totalElementCount <= 44) {
          pointWidth = 5
          groupPadding = 0.35
        } else if (totalElementCount > 44 && totalElementCount <= 60) {
          pointWidth = 4
          groupPadding = 0.3
        }
      } else if (seriesLength == 4) {
        if (totalElementCount <= 4) {
          groupPadding = 0.46
        } else if (totalElementCount > 4 && totalElementCount <= 12) {
          groupPadding = 0.41
        } else if (totalElementCount > 12 && totalElementCount <= 30) {
          groupPadding = 0.38
        } else if (totalElementCount > 30 && totalElementCount <= 44) {
          pointWidth = 5
          groupPadding = 0.35
        } else if (totalElementCount > 44 && totalElementCount <= 60) {
          pointWidth = 4
          groupPadding = 0.3
        }
      } else if (seriesLength == 5) {
        pointWidth = 5
        if (totalElementCount <= 5) {
          groupPadding = 0.45
        } else if (totalElementCount > 5 && totalElementCount <= 10) {
          groupPadding = 0.44
        } else if (totalElementCount > 10 && totalElementCount <= 30) {
          groupPadding = 0.38
        } else if (totalElementCount > 30 && totalElementCount <= 40) {
          pointWidth = 5
          groupPadding = 0.35
        } else if (totalElementCount > 30 && totalElementCount <= 40) {
          pointWidth = 5
          groupPadding = 0.3
        } else if (totalElementCount > 40 && totalElementCount <= 60) {
          pointWidth = 5
          groupPadding = 0.28
        }
      } else if (seriesLength == 6) {
        pointWidth = 5
        if (totalElementCount <= 12) {
          groupPadding = totalElementCount <= 6 ? 0.45 : 0.41
        } else if (totalElementCount > 12 && totalElementCount <= 24) {
          groupPadding = totalElementCount <= 18 ? 0.35 : 0.3
        } else if (totalElementCount > 24 && totalElementCount <= 30) {
          groupPadding = 0.3
        } else if (totalElementCount > 30 && totalElementCount <= 44) {
          pointWidth = 5
          groupPadding = 0.28
        } else if (totalElementCount > 44 && totalElementCount <= 60) {
          pointWidth = 5
          groupPadding = 0.25
        }
      } else if (seriesLength == 7 || seriesLength === 8) {
        // for group over 3, we don't need any space between column, not enough space on the page
        groupPadding = 0
        pointWidth = 5
        if (totalElementCount <= 7) {
          groupPadding = 0.45
        } else if (totalElementCount > 7 && totalElementCount <= 14) {
          groupPadding = 0.41
        } else if (totalElementCount > 14 && totalElementCount <= 30) {
          groupPadding = 0.39
        } else if (totalElementCount > 30 && totalElementCount <= 45) {
          pointWidth = 4
          groupPadding = 0.3
        } else if (totalElementCount > 45 && totalElementCount <= 60) {
          pointWidth = 4
          groupPadding = 0.25
        }
      } else {
        groupPadding = 0
        pointWidth = 4
      }

      if (seriesLength == childMITKRCount) {
        pointWidth = 10
        groupPadding = 0.5
        if (totalElementCount > 30 && totalElementCount <= 45) {
          pointWidth = 4
          groupPadding = 0.3
        } else if (totalElementCount > 45 && totalElementCount <= 60) {
          pointWidth = 4
          groupPadding = 0.25
        }
      }
      if (totalElementCount > 60) {
        pointWidth = 4
        groupPadding = 0
      }
    }
  }

  pointWidth = 10
  let columnPlotOptions = {
    // grouping: true,
    // pointWidth,
    // groupPadding,
    // pointPadding: 0,
    // borderWidth: 0,
  }

  const labelRotation =
    interval == krdetailConstants.INTERVAL_DAY ||
    interval == krdetailConstants.INTERVAL_WEEK
      ? -45
      : 0
  columnPlotOptions = Object.assign({}, columnPlotOptions, {
    stacking: 'normal',
    negativeColor: '#142D4D',
  })

  const orgConfigPeriod = chartOptions.orgConfigPeriod || false
  let dataChart = []
  Array.isArray(seriesDataJson[0].data)
    ? seriesDataJson[0].data.forEach(function (val, ind) {
        if (val[1]) {
          let indVal = 0;
          const chartArr = Array.isArray(val[1]) ? val[1] : [val[1]];
          chartArr.forEach(function (vall, indd) {
            if (typeof dataChart[indVal] === 'undefined') {
              dataChart[indVal] = {}
            }
            let color = '#DCE0EC'
            let seriesName = 'No response'
            if (indd == 0) {
              color = '#3BC1E5'
              seriesName = 'Yes'
            } else if (indd == 1) {
              color = '#142D4D'
              seriesName = 'No'
            }
            let arr = []
            arr.push(val[0])
            arr.push(vall)
            if (typeof dataChart[indVal]['data'] === 'undefined') {
              dataChart[indVal]['data'] = []
            }
            dataChart[indVal]['data'].push(arr)
            dataChart[indVal]['color'] = color
            dataChart[indVal]['name'] = seriesName
            indVal++
          })
        }
      })
    : (dataChart = seriesDataJson)

  const options = {
    chart: {
      renderTo: renderId,
      className: 'kr-det-page-stacked-bar-charts ',
      type: 'column',
      plotBackgroundColor: '#F6F7F9',
      plotBorderWidth: 1,
      plotBorderColor: '#eceff2',
      height,
      zoomType: 'x',
      resetZoomButton: {
        position: {
          x: -15,
          y: 15,
        },
        relativeTo: 'chart',
      },
      events: {
        load() {
          $(`#${renderId}`).data('range', {
            start: this.xAxis[0].dataMin,
            end: this.xAxis[0].dataMax,
          })
          if (
            typeof range.start === 'undefined' &&
            typeof range.end === 'undefined'
          ) {
            minX = this.xAxis[0].dataMin
            maxX = this.xAxis[0].dataMax
          }
          if (minX !== undefined && minX != null) {
            let dateText = moment.utc(minX).format('MMM DD, YYYY')
            if (maxX !== 'undefined' && maxX != null) {
              const endDateText = moment.utc(maxX).format('MMM DD, YYYY')
              dateText = `${dateText} - ${endDateText}`
            }
            const renderCalId = $(`#${renderId}`)
              .parents('.kr-det-graph-div')
              .find('.daterangecomp-text-wrapper')
            //$(renderCalId).find('input').val(dateText);
            //$(renderCalId).find('button').text(dateText);
          }
        },
      },
    },
    title: {
      text: '',
    },
    lang: {
      accessibility: {
        chartContainerLabel:
          chartOptions['titleText'] +
          '. Use focus, form, or scan mode to navigate each data point on the chart with your left and right arrow keys. Use up and down arrows to switch between multi-line data points.',
      },
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
      showTable: chartOptions['displayOptions']['table'],
      csv: {
        // Uncomment for custom column header formatter.
        // This function is called for each column header.
        columnHeaderFormatter: function (item, key) {
          if (!item || item instanceof Highcharts.Axis) {
            return i18next.t('Date')
          } else {
            return item.name
          }
        },
        //
        dateFormat: '%b %e, %Y',
      },
    },
    legend: {
      enabled: false,
    },
    xAxis: {
      type: 'datetime',
      // tickWidth: 1,
      //startOnTick: interval == krdetailConstants.INTERVAL_WEEK ? true : false,
      startOfWeek:
        interval == krdetailConstants.INTERVAL_WEEK
          ? chartOptions.weekly_update_day
            ? parseInt(chartOptions.weekly_update_day)
            : 5
          : 1,
      tickLength: 0,
      tickInterval: tickInterval,
      // min: minX,
      ordinal: false,
      labels: {
        style: {
          fontSize: '10px',
          color: '#666',
        },
        rotation: labelRotation,
        formatter() {
          const labelTime = moment(this.value).utc()._d.getTime()
          const displayLabel = Highcharts.dateFormat(xDateFormat, labelTime)
          return displayLabel
        },
      },
      lineColor: '#eceff2',
      gridLineColor: '#eceff2',
      tickPositioner: function tickPositioner() {
        const ticks = generateTicks({ customSeries: dataChart || [], series: this.series, interval, tickInterval })

        if (ticks?.length) {
          ticks.info = this.series[0].currentDataGrouping
          if (interval == krdetailConstants.INTERVAL_WEEK && minX > ticks[0]) {
            ticks.shift()
          }
          this.min = ticks[0]
          this.dataMin = this.min
          return ticks
        }
      },
    },
    yAxis: {
      visible: SHOW_Y_AXIS,
      plotLines: [],
      title: null,
      tickWidth: 1,
      tickLength: 5,
      tickColor: '#eceff2',
      labels: {
        align: 'center',
        style: {
          fontSize: '10px',
          color: '#666',
        },
        formatter:  function() {
          let symbol = ((chartOptions.metric == krdetailConstants.METRIC_PERCENTAGE) || (chartOptions.counting == krdetailConstants.METRIC_COUNTING_SURVEY_YES_NO && (chartOptions.measure_metric_data == 3 || chartOptions.measure_metric_data == 4) ))? '%' : '';
          return `${formatNumber(this.value) + symbol}`;
        },
      },
      lineColor: '#eceff2',
      gridLineColor: '#eceff2',
    },
    plotOptions: {
      column: columnPlotOptions,
      series: {
        connectNulls: true,
        pointWidth: 18,
        pointPadding: 0.25,
        dataGrouping: {
          forced: true,
          enabled:
            chartOptions.upd_data_interval != chartOptions.data_interval
              ? true
              : false,
          units: units,
        },
        states: {
          inactive: {
            opacity: 0.7,
          },
        },
      },
    },
    series: dataChart || [],
    rangeSelector: {
      enabled: false,
    },
    scrollbar: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      useHTML: true,
      borderRadius: 5,
      shadow: false,
      backgroundColor: 'rgba(255,255,255,1)',
      formatter() {
        const labelTime = moment(this.x).utc()._d.getTime()
        const date = Highcharts.dateFormat(xDateFormat, labelTime)
        const value = formatNumber(this.y)
        let xVal = this.x / 1000
        let sname = this.series.name
        let color = sname == 'Yes' ? 'green' : sname == 'No' ? 'red' : 'gray'
        let comment = chartOptions.showAttachment
          ? chartOptions.tooltip[xVal]
          : ''
        comment = typeof comment !== 'undefined' ? comment : ''
        let width = getToolTipWidth(comment);
        return `<div style='color: ${color}; min-width:${width};' class='krDetailsGraphFont'><span> ${value} - ${sname}</span> <br/> ${date} <br /> ${comment}</div>`
      },
    },
  }

  const myChart = new Highcharts.Chart(options)
}

const cumulativeProgressText = (
  options = {},
  annualGraph = false,
  quarterGraph = false
) => {
  let text =i18next.t('Cumulative progress')
  const { isAnnualMit, isChildMit } = options
  if (isAnnualMit && annualGraph) {
    text = i18next.t('Cumulative annual progress')
  } else if ((isAnnualMit || isChildMit) && quarterGraph) {
    text = i18next.t('Cumulative quarter progress')
  }
  return text
}

const drawStackedTargetBarCharts = (
  renderId,
  chartOptions,
  range = {},
  showLegends = false,
  isChildMit,
  pageSource
) => {
  // interval: 1 daily, 2 weekly, 3 monthly, 4 quarterly
  const totalNumberOfdays = Math.ceil(
    moment(new Date(chartOptions.end_date * 1000)).diff(
      moment(new Date(chartOptions.start_date * 1000)),
      'days',
      true
    )
  )
  const annualGraph = totalNumberOfdays >= 360
  const quarterGraph = totalNumberOfdays >= 88
  const interval = chartOptions.upd_data_interval
  const xDateFormat =
    interval == krdetailConstants.INTERVAL_DAY || isWeeklyInterval(interval)
      ? '%b %e'
      : '%b'
  const height = 233
  let groupPadding = 0.2
  let pointWidth = 10 // default bar width
  const { childMitData = []} = chartOptions;
  let isLastUpdateValue = false;
  const isLastUpdateArray = [];
  if (Array.isArray(childMitData) && childMitData.length) {
    isLastUpdateValue = (typeof childMitData[0]['progress_type'] != 'undefined'
      && childMitData[0]['progress_type'] == 1)
      ? true : false;

      childMitData
            .forEach((item, index) =>
            {
              isLastUpdateArray[index] = (typeof item['progress_type'] != 'undefined'
              && item['progress_type'] == 1)
              ? true : false;
            })
  }
  const seriesData = constructSeries(chartOptions, range, isLastUpdateValue, isLastUpdateArray) || []
  const seriesLength = seriesData.length // how many data group we have
  let seriesDataJson = JSON.parse(JSON.stringify(seriesData))
  let isExceedSeriesDataLimit = false

  let tickInterval = getTickInterval(interval)
  const units = getIntervalUnits(interval)
  let minX =
    typeof range.start !== 'undefined'
      ? moment.utc(range.start)._d.getTime()
      : typeof chartOptions.start_date !== 'undefined' &&
        chartOptions.start_date != null
      ? moment.utc(chartOptions.start_date * 1000)._d.getTime()
      : null

  let maxX =
    typeof range.end !== 'undefined'
      ? moment.utc(range.end)._d.getTime()
      : typeof chartOptions.end_date !== 'undefined' &&
        chartOptions.end_date != null
      ? moment.utc(chartOptions.end_date * 1000)._d.getTime()
      : null

  let xAxisLables = []
  let isRangeDate = false
  let seriesDataCount = 0
  if (typeof range.start !== 'undefined') {
    isRangeDate = true
  }
  let childMITKRCount = 0
  if (Array.isArray(seriesDataJson)) {
    const seriesDataLimit = 400
    seriesDataJson = seriesDataJson.map((s) => {
      if (typeof s.childMIT !== 'undefined' && s.childMIT == true) {
        childMITKRCount++
      }
      if (typeof s.data !== 'undefined' && Array.isArray(s.data)) {
        //s.data = s.data.filter(fd => fd[0] >= minX && fd[0] <= maxX);
        /* if data count is over 30, show the latest 30 data points */
        if (!isRangeDate && s.data.length > seriesDataLimit) {
          isExceedSeriesDataLimit = true
          s.data = s.data.splice(-seriesDataLimit)
        }
        seriesDataCount = seriesDataCount + s.data.length
        xAxisLables = [].concat(...xAxisLables, ...s.data.map((dd) => dd[0]))
      }
      return s
    })
    xAxisLables = [...new Set(xAxisLables)]

    minX = xAxisLables.length ? Math.min(...xAxisLables) : null
    maxX = xAxisLables.length ? Math.max(...xAxisLables) : null
    if (xAxisLables.length) {
      const seriesDataLength = xAxisLables.length // how many data point on first data group
      const totalElementCount = seriesDataCount
      if (seriesLength == 1) {
        groupPadding = 0.47
      } else if (seriesLength == 2) {
        if (totalElementCount <= 2) {
          groupPadding = 0.48
        } else if (totalElementCount > 2 && totalElementCount <= 4) {
          groupPadding = 0.45
        } else if (totalElementCount > 4 && totalElementCount <= 6) {
          groupPadding = 0.43
        } else if (totalElementCount > 6 && totalElementCount <= 10) {
          groupPadding = 0.41
        } else if (totalElementCount > 10 && totalElementCount <= 14) {
          groupPadding = 0.38
        } else if (totalElementCount > 14 && totalElementCount <= 30) {
          groupPadding = 0.32
        } else if (totalElementCount > 30 && totalElementCount <= 44) {
          pointWidth = 5
          groupPadding = 0.35
        } else if (totalElementCount > 44 && totalElementCount <= 60) {
          pointWidth = 4
          groupPadding = 0.3
        }
      } else if (seriesLength == 3) {
        if (totalElementCount <= 6) {
          groupPadding = totalElementCount <= 3 ? 0.47 : 0.45
        } else if (totalElementCount > 6 && totalElementCount <= 14) {
          groupPadding = 0.41
        } else if (totalElementCount > 14 && totalElementCount <= 24) {
          groupPadding = 0.38
        } else if (totalElementCount > 24 && totalElementCount <= 44) {
          pointWidth = 5
          groupPadding = 0.35
        } else if (totalElementCount > 44 && totalElementCount <= 60) {
          pointWidth = 4
          groupPadding = 0.3
        }
      } else if (seriesLength == 4) {
        if (totalElementCount <= 4) {
          groupPadding = 0.46
        } else if (totalElementCount > 4 && totalElementCount <= 12) {
          groupPadding = 0.41
        } else if (totalElementCount > 12 && totalElementCount <= 30) {
          groupPadding = 0.38
        } else if (totalElementCount > 30 && totalElementCount <= 44) {
          pointWidth = 5
          groupPadding = 0.35
        } else if (totalElementCount > 44 && totalElementCount <= 60) {
          pointWidth = 4
          groupPadding = 0.3
        }
      } else if (seriesLength == 5) {
        pointWidth = 5
        if (totalElementCount <= 5) {
          groupPadding = 0.45
        } else if (totalElementCount > 5 && totalElementCount <= 10) {
          groupPadding = 0.44
        } else if (totalElementCount > 10 && totalElementCount <= 30) {
          groupPadding = 0.38
        } else if (totalElementCount > 30 && totalElementCount <= 40) {
          pointWidth = 5
          groupPadding = 0.35
        } else if (totalElementCount > 30 && totalElementCount <= 40) {
          pointWidth = 5
          groupPadding = 0.3
        } else if (totalElementCount > 40 && totalElementCount <= 60) {
          pointWidth = 5
          groupPadding = 0.28
        }
      } else if (seriesLength == 6) {
        pointWidth = 5
        if (totalElementCount <= 12) {
          groupPadding = totalElementCount <= 6 ? 0.45 : 0.41
        } else if (totalElementCount > 12 && totalElementCount <= 24) {
          groupPadding = totalElementCount <= 18 ? 0.35 : 0.3
        } else if (totalElementCount > 24 && totalElementCount <= 30) {
          groupPadding = 0.3
        } else if (totalElementCount > 30 && totalElementCount <= 44) {
          pointWidth = 5
          groupPadding = 0.28
        } else if (totalElementCount > 44 && totalElementCount <= 60) {
          pointWidth = 5
          groupPadding = 0.25
        }
      } else if (seriesLength == 7 || seriesLength === 8) {
        // for group over 3, we don't need any space between column, not enough space on the page
        groupPadding = 0
        pointWidth = 5
        if (totalElementCount <= 7) {
          groupPadding = 0.45
        } else if (totalElementCount > 7 && totalElementCount <= 14) {
          groupPadding = 0.41
        } else if (totalElementCount > 14 && totalElementCount <= 30) {
          groupPadding = 0.39
        } else if (totalElementCount > 30 && totalElementCount <= 45) {
          pointWidth = 4
          groupPadding = 0.3
        } else if (totalElementCount > 45 && totalElementCount <= 60) {
          pointWidth = 4
          groupPadding = 0.25
        }
      } else {
        groupPadding = 0
        pointWidth = 4
      }

      if (seriesLength == childMITKRCount) {
        pointWidth = 10
        groupPadding = 0.5
        if (totalElementCount > 30 && totalElementCount <= 45) {
          pointWidth = 4
          groupPadding = 0.3
        } else if (totalElementCount > 45 && totalElementCount <= 60) {
          pointWidth = 4
          groupPadding = 0.25
        }
      }
      if (totalElementCount > 60) {
        pointWidth = 4
        groupPadding = 0
      }
    }
  }

  const customSeries = []
  // get target color
  const targetColor = chartOptions.target_color || '#cccccc'
  const targetLabel = chartOptions.target_label || 'Target'
  if (xAxisLables.length) {
    xAxisLables = xAxisLables.sort()
  }
  const xBaseSeries = xAxisLables || []
  const seriesDataLength = xBaseSeries.length || 0 // how many data point on first data set
  const totalPoints = seriesLength * seriesDataLength // total columns on the chart
  pointWidth = 12
  let targetWidth = 20
  let pointPlacementSpacing = 0.3
  // point placement spacing is for spacing between each data group column
  if (seriesLength >= 4) {
    pointPlacementSpacing = 0.1
  }
  let pointPlacement =
    0 + -1 * pointPlacementSpacing * Math.floor(seriesLength / 2)
  // pointWidth is for the actual bar column width, targetWidth is for the target column width
  if (pageSource && showLegends) {
    pointWidth = 30
    targetWidth = 30
  } else if (showLegends) {
    pointWidth = 20
    targetWidth = 20
  }

  let labelRotation =
    interval == krdetailConstants.INTERVAL_DAY ||
    interval == krdetailConstants.INTERVAL_WEEK ||
    interval == krdetailConstants.INTERVAL_BIWEEKLY
      ? -45
      : undefined
  if (
    (chartOptions.data_interval == krdetailConstants.INTERVAL_WEEK &&
      (interval == krdetailConstants.INTERVAL_QUARTER ||
        interval == krdetailConstants.INTERVAL_MONTH)) ||
    (chartOptions.data_interval == krdetailConstants.INTERVAL_DAY &&
      (interval == krdetailConstants.INTERVAL_QUARTER ||
        interval == krdetailConstants.INTERVAL_MONTH)) ||
    (chartOptions.data_interval == krdetailConstants.INTERVAL_BIWEEKLY &&
      (interval == krdetailConstants.INTERVAL_QUARTER ||
        interval == krdetailConstants.INTERVAL_MONTH))
  ) {
    if (pageSource && showLegends) {
      pointWidth = 30
      targetWidth = 30
    } else if (showLegends) {
      pointWidth = 20
      targetWidth = 20
    }
  } else if (totalPoints >= 30) {
    pointWidth = 3
    targetWidth = 6
  } else if (totalPoints >= 21) {
    pointWidth = 4
    targetWidth = 7
  } else if (totalPoints >= 18) {
    pointWidth = 7
    targetWidth = 14
  } else if (totalPoints >= 12) {
    pointWidth = 20
    targetWidth = 20
  }
  const actualData = []
  const targetData = []
  const quarterLables = []
  // let cumulativeProgress = 0;
  // let cumulativeTarget = 0;
  // achieve_target
  seriesDataJson.map((obj) => {
    const temp = Object.assign({}, obj)
    // const actualData = [];
    actualData.length = xBaseSeries.length // init data length as base series
    //actualData.fill(null);
    // const targetData = [];
    targetData.length = xBaseSeries.length
    // targetData.fill(null);
    for (const data of temp.data) {
      const index = xBaseSeries.indexOf(data[0])
      if (index !== -1) {
        actualData[index] = { x: data[0], y: data[1] }
        // if(showLegends){
        //   cumulativeProgress+=data[1];
        // }
        if (showLegends) {
          targetData[index] = { x: data[0], y: data[2] }
          quarterLables[data[0]] =
            typeof data[3] !== 'undefined' ? data[3] : null
        } else if (obj.counting === krdetailConstants.METRIC_COUNTING_RANGE) {
          // range kr
          targetData[index] = [obj.lower_limit, obj.upper_limit]
        } else {
          targetData[index] =
            typeof data.target !== 'undefined'
              ? parseInt(data.target)
              : obj.target
        }
        // // cumulative
        // if( showLegends){
        //   cumulativeTarget+=targetData[index];
        // }
      }
    }
    // cumulative label is increasing the xBaseSeries(xAxisLables)
    // So adding the Cumulative data at end element i.e. (length -1)
    if (showLegends) {
      const lastItem = xBaseSeries[xBaseSeries.length - 1]
      let xCumulativeTimeSpan = ''
      if (interval == krdetailConstants.INTERVAL_DAY) {
        xCumulativeTimeSpan = moment(lastItem).add(1, 'w').valueOf()
      } else if (interval == krdetailConstants.INTERVAL_WEEK) {
        xCumulativeTimeSpan = moment(lastItem).add(1, 'w').valueOf()
      } else if (interval == krdetailConstants.INTERVAL_BIWEEKLY) {
        xCumulativeTimeSpan = moment(lastItem).add(2, 'w').valueOf()
      } else if (interval == krdetailConstants.INTERVAL_MONTH) {
        xCumulativeTimeSpan = moment(lastItem).add(1, 'M').valueOf()
      } else if (interval == krdetailConstants.INTERVAL_QUARTER) {
        xCumulativeTimeSpan = moment(lastItem).add(1, 'Q').valueOf()
      }
      actualData[actualData.length] = {
        x: xCumulativeTimeSpan,
        y: Number(chartOptions.achieve_target),
        iscumulative: true,
      } //Number(chartOptions.achieve_target);
      targetData[targetData.length] = {
        x: xCumulativeTimeSpan,
        y: Number(chartOptions.target),
        iscumulative: true,
      } //Number(chartOptions.target);
    }
    let krIndex = 0
    const actualSeriesObj = {
      name: 'Actual',
      color: obj.color,
      data: actualData,
      pointPadding: 0.3,
      pointPlacement,
      pointWidth,
      type: 'column',
      // dataGrouping: {
      //   forced: true,
      //   enabled: chartOptions.upd_data_interval != chartOptions.data_interval ? true : false,
      //   units:units,
      //   approximation: showLegends ? function(arr) {
      //     var start = this.dataGroupInfo.start;
      //     const { childMitData, upd_data_interval, isChildAnnualMit } = chartOptions;
      //     const { iscumulative = false } = this.options.data[start];
      //     console.log('options',  this.options, arr, chartOptions)
      //     let sum = arr.reduce((a, b) => a + b, 0)
      //     if (!iscumulative) {
      //       if (start == 0) {
      //         krIndex = start;
      //       }
      //       if (upd_data_interval == krdetailConstants.INTERVAL_QUARTER
      //          && childMitData && childMitData[krIndex]) {
      //         let { start_date, end_date } = childMitData[krIndex];
      //         start_date = parseInt(start_date) * 1000;
      //         end_date = parseInt(end_date) * 1000;
      //         console.log('start_date', start_date, end_date)
      //         sum = 0;
      //         this.options.data.map((d) => {
      //           if (d.x >= start_date && d.x <= end_date && !d.iscumulative) {
      //             sum += (d.y ? d.y : 0);
      //           }
      //         })
      //         krIndex++;
      //       }
      //     }
      //     return sum;
      //   } : 'close'
      // }
    }
    let targetSeriesObj = {
      name: targetLabel,
      color: targetColor,
      data: targetData,
      pointPadding: 0.4,
      pointPlacement,
      pointWidth: targetWidth,
      type: 'column',
      // dataGrouping: {
      //   forced: true,
      //   enabled: chartOptions.upd_data_interval != chartOptions.data_interval ? true : false,
      //   units:units,
      //   approximation: 'close' //showLegends ? 'sum' : 'close'
      // }
    }
    if (obj.counting === '4') {
      targetSeriesObj = {
        name: targetLabel,
        color: targetColor,
        data: targetData,
        pointPadding: 0.4,
        pointPlacement,
        pointWidth: targetWidth,
      }
    }
    pointPlacement += pointPlacementSpacing
    customSeries.push(targetSeriesObj)
    customSeries.push(actualSeriesObj)
  })

  const orgConfigPeriod = chartOptions.orgConfigPeriod || false

  let legendsConfig = {
    enabled: false,
  }
  if (showLegends) {
    legendsConfig = {
      enabled: true,
      align: 'left',
      verticalAlign: 'top',
      x: -10,
      y: pageSource ? -130 : -78,
      symbolHeight: 12,
      symbolWidth: 12,
      symbolRadius: 0,
      reversed: true,
      itemStyle: {
        fontWeight: 'normal',
        fontSize: '12px',
        fontFamily: 'Lato',
        color: '#596373',
      },
      itemHoverStyle: {
        cursor: 'default',
      },
    }
  }

  // The below code is a temporary fix which works only for table view, this has to be removed while rebuilding the chart
  if (
    chartOptions['displayOptions'] &&
    chartOptions['displayOptions']['table']
  ) {
    customSeries.forEach((item, i) => {
      item.data &&
        item.data.map((data, j) => {
          data.x = data.iscumulative
            ? 'Cumulative'
            : moment.utc(data.x).format('MM-DD-YYYY')
        })
    })
  }

  let cumulativeXTimeStamp
  const isAnnualMitKR = isAnnualMITQuarter(chartOptions.quarter_target_type)
  const targetChartOptions = {
    chart: {
      renderTo: renderId,
      className: 'kr-det-page-target-bar-charts ',
      type: 'columnrange',
      plotBackgroundColor: '#fff',
      plotBorderWidth: 1,
      plotBorderColor: '#fff',
      height,
    },
    title: {
      text: '',
    },
    lang: {
      accessibility: {
        chartContainerLabel:
          chartOptions['titleText'] +
          '. Use focus, form, or scan mode to navigate each data point on the chart with your left and right arrow keys. Use up and down arrows to switch between multi-line data points.',
      },
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
      showTable: chartOptions['displayOptions']['table'],
      csv: {
        // Uncomment for custom column header formatter.
        // This function is called for each column header.
        columnHeaderFormatter: function (item, key) {
          if (!item || item instanceof Highcharts.Axis) {
            return i18next.t('Date')
          } else {
            return item.name
          }
        },
        // dateFormat: '%m-%d-%Y'
      },
    },
    legend: legendsConfig,
    xAxis: {
      // type: 'datetime',
      tickLength: 5,
      tickWidth: 1,
      lineColor: '#eceff2',
      gridLineColor: '#eceff2',
      //categories: xAxisCategories,
      tickInterval: tickInterval,
      min: minX,
      tickPositioner: function tickPositioner() {
        const ticks = generateTicks({ customSeries, series: this.series, interval, tickInterval })

        if (ticks?.length) {
          const {
            childMitData,
            upd_data_interval,
            isChildMit,
            end_date,
            data_interval,
          } = chartOptions
          if (interval == krdetailConstants.INTERVAL_WEEK && minX > ticks[0]) {
            ticks.shift()
          }
          ticks.info = this.series[0].currentDataGrouping
          this.min = ticks[0]
          this.dataMin = this.min
          return ticks
        }
      },
      labels: {
        style: {
          fontSize: '10px',
        },
        rotation: labelRotation,
        formatter() {
          let getTargetDataCumulative = targetData.find(
            (row) => row.x === this.value
          )
          let getActualDataCumulative = actualData.find(
            (row) => row.x === this.value
          )
          if (
            (getActualDataCumulative && getActualDataCumulative.iscumulative) ||
            (getTargetDataCumulative && getTargetDataCumulative.iscumulative) ||
            this.isLast
          ) {
            if (this.isLast) {
              cumulativeXTimeStamp = this.value
            }
            return cumulativeProgressText(
              chartOptions,
              annualGraph,
              quarterGraph
            )
          }
          if (typeof this.value == 'number') {
            if (
              chartOptions.upd_data_interval ==
                krdetailConstants.INTERVAL_QUARTER &&
              showLegends
            ) {
              if (
                isAnnualMitKR ||
                (typeof chartOptions.isChildAnnualMit !== undefined &&
                  chartOptions.isChildAnnualMit)
              ) {
                const qLabel = quarterLables[this.value] || null
                // if (chartOptions.data_interval == krdetailConstants.INTERVAL_QUARTER && qLabel != null && qLabel) {
                //   return qLabel;
                // }
                if (qLabel != null && qLabel) {
                  return qLabel
                }
                const l = findQuarterLabel(chartOptions, this.value)
                if (l != null) {
                  return l
                }
              }
              return Highcharts.dateFormat(xDateFormat, this.value)
            }
            const xTimestamp = this.value
            const labelTime =
              chartOptions.upd_data_interval ==
                krdetailConstants.INTERVAL_QUARTER && !orgConfigPeriod
                ? moment.utc(xTimestamp).add(2, 'months').valueOf()
                : moment(xTimestamp).utc()._d.getTime()
            return Highcharts.dateFormat(xDateFormat, labelTime)
          } else {
            return this.value
          }
        },
      },
    },
    yAxis: {
      visible: SHOW_Y_AXIS,
      title: null,
      tickWidth: 0,
      tickLength: 5,
      tickColor: '#eceff2',
      labels: {
        align: 'center',
        style: {
          fontSize: '10px',
          color: '#666',
        },
        formatter:  function() {
          let symbol = chartOptions.metric == krdetailConstants.METRIC_PERCENTAGE ? '%' : '';
          return `${formatNumber(this.value) + symbol}`;
        },
      },
      lineColor: '#eceff2',
      gridLineColor: '#eceff2',
    },
    plotOptions: {
      column: {
        grouping: false,
        shadow: false,
        borderWidth: 0,
        // pointWidth: 64
      },
      series: {
        connectNulls: true,
        events: {
          legendItemClick: function () {
            return false
          },
        },
        states: {
          inactive: {
            opacity: 0.7,
          },
        },
      },
    },
    series: customSeries,
    rangeSelector: {
      enabled: false,
    },
    scrollbar: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      useHTML: true,
      borderRadius: 4,
      borderColor: '#2c9ab7',
      shadow: false,
      shared: showLegends ? true : false,
      //followPointer: showLegends ? true : false,
      shape: 'rect',
      padding: 10,
      //outside: showLegends ? true : false,
      backgroundColor: '#ffffff',

      positioner: function (labelWidth, labelHeight, point) {
        if (showLegends) {

          /**
           * By default, point.plotX will align the tooltip with the left edge of the column.
           * The goal is to shift the tooltip in the right direction
           * point.plotY will align the tooltip with the top of the column.
           */
          let xOffset, yOffset
          const padding = this.chart?.tooltip?.options?.padding ?? 0
          if (this.chart?.hoverPoint?.options?.iscumulative ?? false) {
            // right align to the column.  shapeArgs.width is the width of the column
            xOffset = labelWidth + (this.chart?.hoverPoint?.shapeArgs?.width ?? 0)
            // lower tooltip so tail aligns with top of column
            yOffset = (labelHeight - padding) / 2
          } else {
            /**
             * NOTE, the little callout "tail" on the tool tip is not exactly centered in the box.
             * This is due to its position being controlled via the text's alignment (left) and
             * a ::before pseudo element.
             * So the tooltip will never be perfectly centered on the column.
             */

            // center with the column
            xOffset = (labelWidth - (padding * 2)) / 2
            // above the column
            yOffset = labelHeight
          }

          // plotLeft contains the width of the Y axis labels.  The width will increase
          // if large values are used for updates
          const xplot = point.plotX - xOffset + this.chart.plotLeft
          // plotTop is not needed as X axis labels are positioned "below" (their y position is higher) the plot
          const yplot = point.plotY - yOffset;

          return {
            x: xplot,
            y: yplot,
          }
        }
        return undefined
      },

      formatter() {
        if (showLegends) {
          let getTargetDataCumulative = targetData.find(
            (row) => row.x === this.x
          )
          let getActualDataCumulative = actualData.find(
            (row) => row.x === this.x
          )
          let actualLabel, targetLabel, isCumulative
          if (
            (getActualDataCumulative && getActualDataCumulative.iscumulative) ||
            (getTargetDataCumulative && getTargetDataCumulative.iscumulative) ||
            this.x === cumulativeXTimeStamp
          ) {
            actualLabel = 'Cumulative'
            targetLabel = 'Cumulative'
            isCumulative = true
          } else {
            targetLabel = getQuarterTooltip(
              chartOptions,
              this.x,
              'Target',
              quarterLables
            )
            actualLabel = getQuarterTooltip(
              chartOptions,
              this.x,
              'Actual',
              quarterLables
            )
            if (!actualLabel) {
              actualLabel = `${Highcharts.dateFormat(xDateFormat, this.x)}`
            }
          }
          const xVal = this.x / 1000
          let comment = chartOptions.showAttachment
            ? chartOptions.tooltip[xVal]
            : ''
          let actualPoint = 0,
            targetPoint = 0
          if (this.points.length) {
            this.points.map((p) => {
              if (p.series.name == 'Target') {
                targetPoint = p.y
              } else if (p.series.name == 'Actual') {
                actualPoint = p.y
              }
            })
          }
          comment = typeof comment !== 'undefined' ? comment : ''
          comment =
            comment.length > 0
              ? `<span style='display:block;border-top:1px solid #C4CBD7;margin-top:8px;padding-top:8px;' >${comment}</span>`
              : ''
          let width =
            comment.length > 50 ||
            targetLabel.length > 9 ||
            actualLabel.length > 9
              ? '150px'
              : '100px'
          return `<div class="stacked-target-chart-tooltip-arrow ${
            pageSource ? '' : 'krdetailspage'
          } ${
            isCumulative ? 'cumulative' : ''
          }" style="width: ${width}; position: relative;">
              <div style="max-height: 105px; overflow-y: auto; line-break: anywhere;" class='krDetailsGraphFont'>
                <div>${actualLabel} actual: ${
            actualPoint && actualPoint > 0
              ? formatNumber(actualPoint)
              : actualPoint
          }</div>
                <div>${targetLabel} target: ${
            targetPoint && targetPoint > 0
              ? formatNumber(targetPoint)
              : targetPoint
          }</div>
                ${comment}
              </span>
            </div>`
        }
        return false
      },
    },
  }

  const myChart = new Highcharts.Chart(targetChartOptions)
}

const drawDonutCharts = (renderId, chartOptions) => {
  const pieHeight = chartOptions.pie_height
  const height = 233
  let dataName = '',
    prefix = ''
  const interval = chartOptions.upd_data_interval;
  const isWeekInterval =
  interval == krdetailConstants.INTERVAL_DAY || isWeeklyInterval(interval)
  const xDateFormat = isWeekInterval ? '%b %e' : '%b'
  if (typeof chartOptions.series[0] !== 'undefined') {
    const { data } = chartOptions.series[0]
    if (Object.keys(data).length) {
      dataName = data[0].name || ''
    }
    let seriesData = chartOptions.series[0].data
    prefix =
      chartOptions.metric == krdetailConstants.METRIC_PERCENTAGE ||
      chartOptions.source == krdetailConstants.SOURCE_DATA_STREAMS
        ? '%'
        : ''
    if (
      seriesData[0] &&
      seriesData[1] &&
      typeof seriesData[0].y !== 'undefined' &&
      typeof seriesData[1].y !== 'undefined' &&
      seriesData[0].y == 0 &&
      seriesData[1].y == 0
    ) {
      chartOptions.series[0].data[1].y = 100
      if (chartOptions.metric == krdetailConstants.METRIC_PERCENTAGE) {
        chartOptions.series[0].data[0].name =
          chartOptions.series[0].data[0].name + prefix
        dataName = chartOptions.series[0].data[0].name =
          chartOptions.series[0].data[0].name == ''
            ? 0
            : chartOptions.series[0].data[0].name
      }
    } else {
      if (
        chartOptions.counting == krdetailConstants.METRIC_COUNTING_DOWN ||
        (chartOptions.source == krdetailConstants.SOURCE_PERSON &&
          chartOptions.progress_type == krdetailConstants.PROGRESS_TYPE_AVERAGE)
      ) {
        dataName = chartOptions.series[0].data[0].name =
          seriesData[0].y + prefix
      } else {
        //let targetVal = (chartOptions.target > 0) ? (seriesData[0].y / chartOptions.target) * 100 : 0;
        let targetVal =
          chartOptions.target > 0
            ? (seriesData[0].y / chartOptions.target) * 100
            : (seriesData[0].y - seriesData[1].y / seriesData[0].y) * 100
        targetVal = targetVal.toFixed(2)
        targetVal = targetVal + prefix
        targetVal = targetVal.replace(/.00%/gi, '%')
        chartOptions.series[0].data[0].name = targetVal
        dataName = targetVal
      }
    }
  }
  // The below code is a temporary fix which works only for table view, this has to be removed while rebuilding the chart
  if (
    chartOptions['displayOptions'] &&
    chartOptions['displayOptions']['table']
  ) {
    chartOptions.series.forEach((item, i) => {
      item.data &&
        item.data.map((data, j) => {
          data.name = data.name == '-Infinity%' ? 'No Data' : data.name
        })
    })
  }
  let quarterLables = [];
  if (seriesData !== undefined) {
    seriesData.forEach((point) => {
      quarterLables[point[0]] =
        typeof point[3] !== 'undefined' ? point[3] : null
    })
  }
  const options = {
    chart: {
      renderTo: renderId,
      className: 'kr-det-page-donut-charts ',
      height,
      type: 'pie',
    },
    title: {
      text: '',
    },
    lang: {
      accessibility: {
        chartContainerLabel:
          chartOptions['titleText'] +
          '. Use focus, form, or scan mode to navigate each data point on the chart with your left and right arrow keys. Use up and down arrows to switch between multi-line data points.',
      },
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
      showTable: chartOptions['displayOptions']['table'],
      csv: {
        columnHeaderFormatter: function (item) {
          if (!item || item instanceof Highcharts.Axis) {
            return 'Percentage'
          } else {
            return item.name == 'Actual' ? 'Done' : item.name
          }
        },
      },
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      useHTML: true,
      borderRadius: 8,
      shadow: false,
      backgroundColor: 'rgba(255,255,255,1)',
      formatter() {
        const value = formatNumber(this.y)
        let date = getQuarterTooltip(
          chartOptions,
          this.x,
          this.series.name,
          quarterLables
        )
        let xVal = this.x / 1000
        let comment = chartOptions.showAttachment && chartOptions.tooltip[xVal] !== undefined
        ? chartOptions.tooltip[xVal]
        : '';
        comment = (comment !== 'undefined')  ? comment : ''
        let width = getToolTipWidth(comment);
        let tooltip = `<div style='min-width:${width}; max-height: 150px; overflow-y: auto;' class='krDetailsGraphFont' data-testid="krDetailsGraphFont">${date} ${this.series.name} :
        ${value ? formatNumber(value) : 0} ${comment}<br/></div>`;
        return tooltip
      },
    },
    plotOptions: {
      pie: {
        animation: false,
        enableMouseTracking: false,
        borderWidth: 0,
        colors: ['#3BC1E5', '#eceff2'],
        dataLabels: {
          useHTML: true,
          autoRotation: 0,
          enabled: true,
          softConnector: false,
          distance: -70,
          style: {
            fontSize: '22px',
            fontFamily:
              '"Lucida Grande", "Lucida Sans Unicode", Arial, Helvetica, sans-serif',
            color: '#3BC1E5',
            fontWeight: 'bold',
            left: '10px',
          },
          formatter: function () {
            return (this.point.percentage >= 0 || this.point.percentage < 0) &&
              this.point.name !== '' &&
              this.point.name !== null
              ? Math.round(this.point.percentage * 100) / 100 +
                  "<span class='testclass'>" +
                  prefix +
                  '</span>'
              : ''
          },
        },
        size: pieHeight
          ? pieHeight * 0.8
          : typeof chartOptions.print_height !== 'undefined'
          ? 144
          : 184,
        innerSize: pieHeight
          ? pieHeight * 0.6
          : typeof chartOptions.print_height !== 'undefined'
          ? 84
          : 124,
        slicedOffset: 0,
        shadow: {
          enabled: true,
          opacity: 1,
          width: 0.7,
        },
      },
    },
    xAxis: {
      ordinal: false,
      type: 'datetime',
      startOnTick: true,
      endOnTick: true,
      tickLength: 5,
      tickWidth: 1,
      maxPadding: 0.2,
      labels: {
        crop: true,
        style: {
          fontSize: '10px',
          color: '#666',
          textOverflow: 'none',
        },

        /**
         * Formats a tick label
         *
         * @return {string} label string
         */
        formatter() {
          const label = this
          if (
            chartOptions.upd_data_interval == krdetailConstants.INTERVAL_QUARTER
          ) {
            if (
              (chartOptions.isChildAnnualMit !== undefined &&
                chartOptions.isChildAnnualMit)
            ) {
              const qLabel = quarterLables[this.value] || null
              if (qLabel != null && qLabel) {
                return qLabel
              }
              const l = findQuarterLabel(chartOptions, this.value)
              if (l != null) {
                return l
              }
            }
            return Highcharts.dateFormat(xDateFormat, this.value)
          }
          const labelTime = moment(label.value).utc()._d.getTime()
          const labelValue = Highcharts.dateFormat(xDateFormat, labelTime)
          return labelValue
        },
      },

      lineColor: '#eceff2',
      gridLineColor: '#eceff2',
    },
    series: chartOptions.series,
  }

  const myChart = new Highcharts.Chart(options)
  // The below code is a temporary fix which works only for the table view, this has to be removed while rebuilding the chart
  if (
    chartOptions['displayOptions'] &&
    chartOptions['displayOptions']['table']
  ) {
    $('.highcharts-data-table table thead th:nth-child(2)').remove()
    $('.highcharts-data-table table tbody')
      .find('tr')
      .each(function (i, el) {
        let $tds = $(this).find('td')
        $tds.remove()
      })
  }
}
const getChartType = (chartType) => {
  // PIE_CHART: 1,
  // BAR_CHART: 2,
  // LINE_CHART: 3,
  // STACKED_BAR_CHART: 5,
  // RANGE_CHART: 6,
  // THRESHOLD_CHART: 7,
  let chartName = ''
  switch (chartType) {
    case '1':
      chartName = 'Pie Chart'
      break
    case '2':
      chartName = 'Bar Chart'
      break
    case '3':
      chartName = 'Line Chart'
      break
    case '5':
      chartName = 'Stacked Bar Chart'
      break
    case '6':
      chartName = 'Range Chart'
      break
    case '7':
      chartName = 'Threshold Chart'
      break
    default:
      chartName = ''
  }
  return chartName
}
const getProgressType = (progressType) => {
  //  PROGRESS_TYPE_LAST_UPDATE: 1,
  //  PROGRESS_TYPE_TOTAL: 2,
  //  PROGRESS_TYPE_AVERAGE: 3,
  //  PROGRESS_TYPE_SUM_LAST_UPDATE: 4,
  let progressName = ''
  switch (progressType) {
    case '1':
      progressName = 'Last Update'
      break
    case '2':
      progressName = 'Total'
      break
    case '3':
      progressName = 'Average'
      break
    case '4':
      progressName = 'Sum Last Update'
      break

    default:
      progressName = ''
  }
  return progressName
}

export const drawMetricCharts = (
  renderId,
  range = {},
  chartOptions = {},
  surveyUserSelected = '',
  pageSource = '',
  display = { table: false, chart: true }
) => {
  if (!$(`#${renderId}`).length) {
    return
  }
  // Setting the defaults for the numerical symbols on the Y-Axis
  Highcharts.setOptions({
    lang: {
      numericSymbols: WOBO_NUMERIC_SYMBOL_DEFAULTS,
    },
  })
  let titleText =
    chartOptions.kr_name +
    ' is displayed as a ' +
    getChartType(chartOptions.graph_type).toLowerCase() +
    ' ' +
    getProgressType(chartOptions.progress_type).toLowerCase()

  chartOptions['titleText'] = titleText ? titleText : ''
  chartOptions['displayOptions'] = display
  if (
    chartOptions.isChildMit ||
    chartOptions.source == krdetailConstants.SOURCE_MIT_PERSON ||
    chartOptions.source == krdetailConstants.SOURCE_MIT_DATA_STREAMS ||
    chartOptions.source == krdetailConstants.SOURCE_DISTINCT_KR ||
    chartOptions.mirrorSource == krdetailConstants.SOURCE_MIT_PERSON ||
    chartOptions.mirrorSource == krdetailConstants.SOURCE_MIT_DATA_STREAMS ||
    chartOptions.mirrorSource == krdetailConstants.SOURCE_DISTINCT_KR
  ) {
    if (chartOptions.progress_type == 2) {
      drawStackedTargetBarCharts(
        renderId,
        chartOptions,
        range,
        true,
        chartOptions.isChildMit,
        pageSource
      )
    } else if(chartOptions.graph_type == krdetailConstants.PIE_CHART) {
      drawDonutCharts(renderId, chartOptions)
    }else {
      chartOptions.series[0] = backfillNullValues(chartOptions.series[0]);
      drawLineCharts(
        renderId,
        chartOptions,
        range,
        true,
        chartOptions.isChildMit,
        pageSource
      )
    }
  } else if (
    chartOptions.graph_type == krdetailConstants.STACKED_BAR_CHART &&
    surveyUserSelected == ''
  ) {
    //
    drawStackedBarCharts(renderId, chartOptions, range)
  } else if (
    chartOptions.graph_type == krdetailConstants.BAR_CHART ||
    (chartOptions.graph_type == krdetailConstants.STACKED_BAR_CHART &&
      surveyUserSelected != '')
  ) {

    drawBarCharts(renderId, chartOptions, range, surveyUserSelected, pageSource)
  } else if (
    chartOptions.graph_type == krdetailConstants.LINE_CHART ||
    chartOptions.graph_type == krdetailConstants.RANGE_CHART ||
    chartOptions.graph_type == krdetailConstants.THRESHOLD_CHART ||
    chartOptions.graph_type == krdetailConstants.PIE_CHART
  ) {
    chartOptions.series[0] = backfillNullValues(chartOptions.series[0]);
    drawLineCharts(renderId, chartOptions, range)
  }
}

const backfillNullValues = (series) => {
  if (series.data.length) {
    let greatestDateWithValue = null;
    series.data.forEach((s) => {
      const xValue = s[0];
      const yValue = s[1];
      if (greatestDateWithValue === null && yValue !== null) {
        greatestDateWithValue = xValue;
      } else if (yValue !== null) {
        greatestDateWithValue = xValue;
      }
    });

    // backfill any null values with the last known value within
    // if the missing value is within the update period
    let lastValue = null;
    for (let i = 0; i < series.data.length; i++) {
      if (series.data[i][0] <= greatestDateWithValue) {
        if (lastValue === null) {
          lastValue = series.data[i][1];
        } else if (series.data[i][1] === null) {
          series.data[i][1] = lastValue;
        }
        lastValue = series.data[i][1];
      }
    }
  }
  return series;
}

const createOrgPeriodsObject = (orgPeriods) => {
  return orgPeriods.map((p, index) => {
    p.start = parseInt(p.start) * 1000
    p.end = (parseInt(p.end) + 86400 - 1) * 1000 // should be 2021-12-2 23:59:59
    p.index = index
    return p
  })
}

const getSumOfValue = (arr) => {
  return arr.reduce((a, b) => {
    const aValue = !isNaN(Number(a)) ? Number(a) : 0
    const bValue = !isNaN(Number(b)) ? Number(b) : 0
    return aValue + bValue
  }, 0)
}

const getLastValue = (arr) => {
  const f = arr.filter((f) => !isNaN(f) && f != null)
  return f.length ? f[f.length - 1] : null
}

export const constructSeries = (chartOptions, range, isLineChart, isLastUpdateArray) => {
  const {
    data_interval,
    upd_data_interval,
    goal_start_date,
    start_date,
    end_date,
    orgPeriods,
  } = chartOptions
  let series = lodash.cloneDeep(chartOptions.series)
  if (series && series[0]) {
    let isRanged = false,
      rangeStart,
      rangeEnd
    if (range && range.start && range.end) {
      isRanged = true
      rangeStart = moment(range.start).unix()
      rangeEnd = moment(range.end).unix()
      let newData = []
      series[0].data.forEach((d) => {
        if (
          d[0] >= moment.unix(rangeStart).valueOf() &&
          d[0] < moment.unix(rangeEnd).valueOf()
        ) {
          newData.push(d)
        }
      })

      series[0].data = newData
      if (series[1]) {
        series[1].data = series[1].data.every((d) => {
          if (
            d[0] >= moment.unix(rangeStart).valueOf() &&
            d[0] < moment.unix(rangeEnd).valueOf()
          ) {
            //newData.push(d);
            return true
          }
          return false
        })
      }

    }
    if (data_interval != upd_data_interval) {
      if (upd_data_interval == krdetailConstants.INTERVAL_QUARTER) {
        let newData = []
        if (Array.isArray(orgPeriods) && orgPeriods.length) {
          const periods = JSON.parse(JSON.stringify(orgPeriods))
          const orgPeriodsArray = createOrgPeriodsObject(periods)
          console.log('orgPeriodsArray', orgPeriodsArray)
          let dataArray = []
          series[0].data.forEach((d) => {
            const findQ = orgPeriodsArray.find(
              (q) => q.start <= parseInt(d[0]) && parseInt(d[0]) <= q.end
            )
            if (findQ) {
              if (!dataArray[findQ.index]) {
                dataArray[findQ.index] = []
              }
              dataArray[findQ.index].push(d[1])
              const sValue = [findQ.end, d[1]]
              if (typeof d[2] !== undefined) {
                sValue.push(d[2])
              }
              if (typeof d[3] !== undefined) {
                sValue.push(findQ._name)
              }
              newData[findQ.index] = sValue
            }
          })
          dataArray = dataArray.filter((da) => da && da.length)
          newData = newData
            .filter((d) => d && d.length)
            .map((n, index) => {
              isLineChart = (typeof isLastUpdateArray != 'undefined' && typeof isLastUpdateArray[index] != 'undefined') ? isLastUpdateArray[index] : isLineChart;
              n[1] = isLineChart
                ? getLastValue(dataArray[index])
                : getSumOfValue(dataArray[index])
              return n
            })
          //console.log('newData 1', newData)
        } else {
          let currentQuarterStart = isRanged
            ? rangeStart
            : chartOptions.isChildMit
            ? start_date
            : goal_start_date
          let currentQuarterEnd = moment
            .unix(currentQuarterStart)
            .add(2, 'month')
            .endOf('month')
            .unix()
          console.log('quarter start: ' + currentQuarterStart)
          console.log('quarter end: ' + currentQuarterEnd)
          let index = 0
          newData = [[moment.unix(currentQuarterEnd).valueOf(), null, 0, '']]
          series[0].data.forEach((d) => {
            if (
              d[0] >= moment.unix(currentQuarterStart).valueOf() &&
              d[0] < moment.unix(currentQuarterEnd).valueOf()
            ) {
              if (d[1] || d[1] === 0) {
                if (!newData[index][1]) {
                  newData[index][1] = 0
                }
                //newData[index][1] = newData[index][1] + Number(d[1]);
                newData[index][1] = isLineChart
                  ? Number(d[1])
                  : newData[index][1] + Number(d[1])
              }
              if (!newData[index][2]) {
                // newData[index][2] = d[2];
                newData[index][2] = getTargetValue(chartOptions, d[0], d[2])
              }
              if (!newData[index][3]) {
                //newData[index][3] = d[3];
                //newData[index][3] =  d[3] ?  d[3] : `Q${index+1}`;
                newData[index][3] = calculateQuarterLabel(chartOptions, d[0])
                console.log(calculateQuarterLabel(chartOptions, d[0]))
              }
            } else {
              currentQuarterStart = moment
                .unix(currentQuarterStart)
                .add(3, 'month')
                .startOf('month')
                .unix()
              currentQuarterEnd = moment
                .unix(currentQuarterStart)
                .add(2, 'month')
                .endOf('month')
                .unix()
              newData.push([
                moment.unix(currentQuarterEnd).valueOf(),
                d[1],
                d[2],
                d[3],
              ])
              index++
              //return true;
            }
          })
        }
        series[0].data = newData;

        if(series[1] && typeof series[1].name !== undefined && series[1].name == 'Target'){
          series[1].data = newData.map((data) => [data[0], data[2]])
        }
      } else if (upd_data_interval == krdetailConstants.INTERVAL_MONTH) {
        let currentMonthStart = isRanged
          ? rangeStart
          : chartOptions.isChildMit
          ? start_date
          : goal_start_date
        currentMonthStart = currentMonthStart * 1000;
        let currentMonthEnd = moment
          .utc(currentMonthStart)
          .endOf('month')
          .valueOf() - 84399999;
        let index = 0
        let newData = [[currentMonthEnd, null, 0, '']]
        series[0].data.forEach((d) => {
          if (
            d[0] >= currentMonthStart &&
            d[0] < currentMonthEnd
          ) {
            if (d[1] || d[1] === 0) {
              if (!newData[index][1]) {
                newData[index][1] = 0
              }
              //newData[index][1] = newData[index][1] + Number(d[1]);
              newData[index][1] = isLineChart
                ? d[1]
                : newData[index][1] + Number(d[1])
            }
            if (!newData[index][2]) {
              // newData[index][2] = d[2];
              newData[index][2] = getTargetValue(chartOptions, d[0], d[2])
            }
            if (!newData[index][3]) {
              newData[index][3] = d[3]
            }
          } else {
            const monthEndValue = moment.utc(d[0])
            .endOf('month')
            .valueOf();
            const findMonth = newData.find((m) => m[0] == monthEndValue);
            const findMonthIndex = newData.findIndex((m) => m[0] == monthEndValue);
            if (!findMonth) {
              newData.push([
                monthEndValue,
                d[1],
                d[2],
                d[3],
              ])
              index++
            }
            else {
              if (typeof newData[findMonthIndex] !== undefined && d[1]) {
                newData[findMonthIndex][1] = isLineChart
                ? d[1]
                : newData[findMonthIndex][1] + Number(d[1])
              }
            }
            //return true;
          }
        })
        series[0].data = newData
        if (series[1] && typeof series[1].name !== undefined && series[1].name == 'Target' ) {
          series[1].data = newData.map((data) => [data[0], data[2]])
        }
      } else if (upd_data_interval == krdetailConstants.INTERVAL_BIWEEKLY) {
        let currentBiWeekStart = isRanged
          ? rangeStart
          : chartOptions.isChildMit
          ? start_date
          : goal_start_date
        let currentBiWeekEnd = moment
          .unix(currentBiWeekStart)
          .add(13, 'days')
          .unix()



        let index = 0
        let newData = [[moment.unix(currentBiWeekStart).valueOf(), null, 0, '']]
        series[0].data.forEach((d) => {
          if (
            d[0] >= moment.unix(currentBiWeekStart).valueOf() &&
            d[0] < moment.unix(currentBiWeekEnd).valueOf()
          ) {
            if (d[1] || d[1] === 0) {
              if (!newData[index][1]) {
                newData[index][1] = 0
              }
              //newData[index][1] = newData[index][1] + Number(d[1]);
              newData[index][1] = isLineChart
                ? Number(d[1])
                : newData[index][1] + Number(d[1])
            }
            if (!newData[index][2]) {
              // newData[index][2] = d[2];
              newData[index][2] = getTargetValue(chartOptions, d[0], d[2])
            }
            if (!newData[index][3]) {
              newData[index][3] = d[3]
            }
          } else {
            currentBiWeekStart = moment
              .unix(currentBiWeekEnd)
              .add(1, 'days')
              .unix()
            currentBiWeekEnd = moment
              .unix(currentBiWeekStart)
              .add(13, 'days')
              .unix()
            newData.push([
              moment.unix(currentBiWeekStart).valueOf(),
              d[1],
              d[2],
              d[3],
            ])
            index++
          }
        })

        series[0].data = newData;
        if(series[1] && typeof series[1].name !== undefined && series[1].name == 'Target'){
          series[1].data = newData.map((data) => [data[0], data[2]])
        }
      } else if (upd_data_interval == krdetailConstants.INTERVAL_WEEK) {
        let currentWeekStart = isRanged
          ? rangeStart
          : chartOptions.isChildMit
          ? start_date
          : goal_start_date
        let currentWeekEnd = moment.unix(currentWeekStart).add(6, 'days').unix()
        let index = 0
        let newData = [[moment.unix(currentWeekStart).valueOf(), null, 0, '']]
        series[0].data.forEach((d) => {
          if (
            d[0] >= moment.unix(currentWeekStart).valueOf() &&
            d[0] < moment.unix(currentWeekEnd).valueOf()
          ) {
            if (d[1] || d[1] === 0) {
              if (!newData[index][1]) {
                newData[index][1] = 0
              }
              //newData[index][1] = newData[index][1] + Number(d[1]);
              newData[index][1] = isLineChart
                ? Number(d[1])
                : newData[index][1] + Number(d[1])
            }
            if (!newData[index][2]) {
              // newData[index][2] = d[2];
              newData[index][2] = getTargetValue(chartOptions, d[0], d[2])
            }
            if (!newData[index][3]) {
              newData[index][3] = d[3]
            }
          } else {
            currentWeekStart = moment.unix(currentWeekEnd).add(1, 'days').unix()
            currentWeekEnd = moment.unix(currentWeekStart).add(6, 'days').unix()
            newData.push([
              moment.unix(currentWeekStart).valueOf(),
              d[1],
              d[2],
              d[3],
            ])
            index++
          }
        })
        series[0].data = newData
        if (series[1] && typeof series[1].name !== undefined && series[1].name == 'Target') {
          series[1].data = newData.map((data) => [data[0], data[2]])
        }
      }
    }
    return series
  }
  return []
}

const calculateQuarterLabel = (chartOptions, time) => {
  const { isChildMit, start_date, goal_start_date } = chartOptions
  let startTime = chartOptions.isChildMit ? start_date : goal_start_date
  let quarterEnd = moment.unix(startTime).add(2, 'month').endOf('month').unix()
  let index = 1

  while (time > moment.unix(quarterEnd).valueOf()) {
    quarterEnd = moment.unix(quarterEnd).add(3, 'month').endOf('month').unix()
    index++
  }
  return `Q${index}`
}

const getTargetValue = (chartOpt, time, target) => {
  const { childMitData, isChildAnnualMit, series, upd_data_interval } = chartOpt
  let newTarget = Number(target)
  if (childMitData) {
    console.log(childMitData)
    console.log(time)
    childMitData.every((child) => {
      if (
        time >= moment.unix(child.start_date).valueOf() &&
        time <= moment.unix(child.end_date).endOf('day').valueOf() &&
        child.target
      ) {
        newTarget = Number(child.target)
        return false
      }
      return true
    })
  }
  return newTarget
}

const getToolTipWidth = (comment) => {
  let width = '30px';
  let commentLength = comment.length;
  if(commentLength > 0){
    if(commentLength > 100){
      width = '200px';
    }else if(commentLength >75 && commentLength <= 100){
      width = '150px';
    }else if(commentLength >50 && commentLength <= 75){
      width = '100px';
    }else if(commentLength > 30 && commentLength <= 50){
      width = '75px';
    }else if(commentLength > 10 && commentLength <= 30){
      width = '50px';
    }else{
      width = '35px';
    }
  }
  return width;
}